/**
 * Global action dispatcher manager
 *
 * @author Zarko Radojevic
 * @contributors Branislav Bogdanovic
 */
import CookieUtil from '../utility/CookieUtil';
import Configuration from './config/Configuration';
import * as _ from 'lodash';
import uuid from 'uuid-random';
import Ajax from './ajax/Ajax';
import { BaseService } from './services/BaseService';
import {
  attach,
  contains, 
  decodeHtmlCharacters,
  error,
  findBy,
  getApiConfig,
  getMergeUpdateObj,
  getSizeByByte,
  insert,
  messageWithColors,
  remove,
  removeAt,
  spliceArray
} from '../utility';
import DispatcherWindowManager from '../utility/window/WindowManager';
import Connect from '../utility/window/Connect';
import {
  ASYNC_DOWNLOAD_DISPATCHER_PARTIAL_MATCH_SPLITTER,
  ASYNC_DOWNLOAD_DISPATCHER_PREFIX,
  ASYNC_UPLOAD_DISPATCHER_NAME,
  DISPATCHER_ALL_UPLOAD_CANCELED,
  DISPATCHER_UPLOAD_CANCELED,
  DOWNLOAD_INDICATOR_WIDGET,
  MAIN_DISPATCHER_ID,
  UPLOAD_REQUEST_CANCEL_SCHEDULED,
  UPLOAD_REQUEST_CANCELED
} from './config/Constants';
import DataProxy from './util/DataProxy';
import PingDownload from './operationtype/PingDownload';
import Upload from './operationtype/Upload';
import {
  Action,
  ActionOperation,
  ActionsObj,
  ActionType,
  ButtonActionConfig,
  ChildrenType,
  CloseOperation,
  ConditionOperation,
  ConsentCookies,
  CookieMethod,
  CookieOperation,
  DialogButton,
  DialogConfig,
  DialogOperation,
  DialogPosition,
  EmptyObject,
  LanguageOperation,
  LoopObjectType,
  MaskOperation,
  MergeAsyncRequest,
  Operation,
  OperationType,
  PingDownloadOperation,
  PopupWindowOperation,
  Position,
  PromiseConfig,
  PromiseOperation,
  RedirectOperation,
  RequestDataBase,
  RequestOperation,
  SignOperation,
  SignType,
  StackOperation,
  Stop,
  StopAllOperation,
  SwitchOperation,
  SyncDispatcherItemsOperation,
  ThemeLoad,
  ThemeOperation,
  UnlinkOperation,
  UnmaskOperation,
  UpdateDialog,
  UpdateDialogScope,
  UpdateStateDispatcherModule,
  UpdateStateModule,
  UploadOperation,
  WarningOperation,
  WarningType, 
  WebSocketFakePingOperation,
  WebSocketOperation,
  WidgetOperation,
  WindowManagerOperation,
  WindowType,
  IsModuleMountedOperation
} from './Types';
// <debug>
import OperationError from './operationtype/OperationError';
// </debug>
import i18n from '../locales/i18n';
import ErrorTrap from '../utility/ErrorTrap';
import FileToLargeContent from './modules/FileToLargeContent';
import Socket from './websockets/Socket';
import { Esapi } from '../index';

type devType = {
  debug?: string | boolean;
  log?: string | boolean;
};

export class DispatcherManager {
  // <debug>
  public static COUNTER: number = 0; // Dispatcher Editor Debug Mode
  private conditionOrder: any = {}; // Dispatcher Editor Debug Mode
  // </debug>
  static readonly EXECUTE_NATIVE_METHOD: string = 'dispatchNativeMethod';
  static readonly MAX_NUMBER_OF_FUNCTION_ARGS: number = 11;
  static readonly DEBUG_EXPRESSION: string = '\u0064\u0065\u0062\u0075\u0067\u0067\u0065\u0072;\n';
  static readonly RESPONSE_OBJECT_DEFAULT_NAME: string = 'responseObject';
  static readonly MERGE_ASYNC_RESPONSE_OBJECT_DEFAULT_NAME: string = 'mergeAsyncResponseObject';
  static readonly RESPONSE_PROMISE_DEFAULT_NAME: string = 'responsePromise';
  static readonly MERGE_ASYNC_REQUEST_PREFIX = uuid() + '_';

  /**
   * OPERATION_PROPERTIES_TO_EVALUATE Operation properties that will be evaluated if evaluate property is passed and it's true.
   * Also used as a list of forbidden properties to add on dispatcher appOperationData in DataProxy class.
   */
  public static OPERATION_PROPERTIES_TO_EVALUATE: string[] = ['moduleName', 'name', 'moduleId'];
  private static GENERIC_ACTION_NAME: string = 'appGenericAction';
  private static DEFAULT_MASK_LOCALIZATION_MSG: string = 'pleaseWait';
  private static DYNAMIC_ACTION_PREFIX: string = 'dynamic_';
  private static PROPERTIES_TO_REMOVE_AFTER_EVERY_REQUEST = ['params', 'urlParams', 'url', 'method', 'concurrently', 'isRest', 'isAuthRest', 'isUpload', 'isDownload', 'downloadType', 'withoutResponse', 'headers', 'handleError', 'usePrefix']; // TODO remove 'usePrefix' after all endpoints prefixes are standardised

  /**
   * UPLOAD holds upload progress and cancel function for every file that is uploading.
   */
  private static UPLOAD: { [key: string]: { CANCEL: { [key: string]: Function }; DATA: { [key: string]: number } } } = {};

  static readonly DEFAULT_ACTION_TYPE: ActionType = {
    canceledStackActions: null,
    delay: 0,
    dispatcherId: MAIN_DISPATCHER_ID,
    loop: null,
    persistent: [],
    pos: Position.end,
    propagationIndependent: null,
    stack: false
  };

  // <debug>
  private appTimeLogParams: {
    moduleName: string;
    log: boolean | string;
    operationActionName: string;
    operationName: string;
    startTime: number;
  } | null = null;

  /**
   * DEVELOPMENT Used only in development mode for easier debugging of operations.
   */
  public DEVELOPMENT = {
    /**
     * ENABLE_OPERATION_DEBUG if true post messages will be sent to Dispatcher Manager Chrome Extension
     */
    ENABLE_OPERATION_DEBUG: false,

    /**
     * ENABLE_OPERATION_TIME_LOGGING if true then every executed operation will be logged.
     */
    ENABLE_OPERATION_TIME_LOGGING: false,

    /**
     * LOG_ACTION_NAMES Keeps information about actions that should be logged with different color.
     */
    LOG_ACTION_NAMES: {} as {
      [key: string]: string;
    },

    /**
     * MESSAGES stacked messages on page reload.
     */
    MESSAGES: [],

    /**
     * OPERATION_LOG_COLOR default color for logging operations with log property.
     */
    OPERATION_LOG_COLOR: 'green',

    /**
     * OPERATION_LOG_DATA if true and there is log property on operation, then clone of appOperationData will be logged with operation.
     */
    OPERATION_LOG_DATA: true
  };

  /**
   * Used in combination with Dispatcher Editor to count syncing data between dispatchers.
   */
  private static syncCounter: {
    [key: string]: number
  } = {};

  // </debug>

  /**
   * Saves classes that will render as widgets, dialogs or widgets children.
   * We do not wan't that kind of data (classes, modules and functions) passed to `appOperationData`, so we store it
   * temporally until widget, dialog or widget is shown.
   */
  private dialogChildren: ChildrenType[] = [];
  appActions: ActionsObj | ProxyHandler<ActionsObj> = {};
  actionsLoaded: boolean = false;
  public Axios: any;
  private webSocket: Socket;
  appCurrentOperation: Operation | null = null;
  appDeferredCounter: EmptyObject = {};
  appDependenceActions: Action = [];
  appDispatcherId: string = MAIN_DISPATCHER_ID;
  appDispatchers: { [key: string]: DispatcherManager } = {};
  appAllPersistentData: {
    [key: string]: boolean;
  } = {};
  appExecutedOperations: Operation[] = [];
  appModules: any = {};

  /**
   * Gets number of active downloads at the moment.
   */
  private numberOfActiveDownloads = (): number => Object.keys(DispatcherManager.Instance.appDispatchers).filter((dispatcherId: string) => dispatcherId.match(new RegExp(ASYNC_DOWNLOAD_DISPATCHER_PREFIX, 'g'))).length;
  private route = (): string => Configuration.getPathname().replace(/^\//, '');

  appOperationData: any = {};
  appSpecialOperationData: any = {};
  appStack: Operation[] = [];
  appWaitingForModule: string = '';

  /**
   * Very seriously property! If true dispatcher is totally stopped until operation is completed.
   * You can use this property in synchronous promises, ajax and delays operations.
   * @private {Boolean} stopExecOperations
   */
  private stopExecOperations: boolean = false;

  private updateStateModules: { [key: string]: UpdateStateDispatcherModule[] } = {};

  private aggregatedResults: any[] = null;

  private static _instance: DispatcherManager;
  public errorTrap: ErrorTrap;
  topLevelComponentName: string = '';
  private static asyncRequestInProgress: Operation[] = [];
  /**
   * Used with operation type mergeAsyncRequest
   */
  private mappedSubNames: string[] = [];

  public static get Instance() {
    return this._instance || (this._instance = new this({}));
  }

  // <debug>
  private addActionInterceptor = () => ({
    get: Reflect.get, // default getter
    set: function (obj, prop, value) {
      DispatcherManager.sendGeneratedActionsToDispatcherEditor({
        [prop]: value
      }, true);
      return Reflect.set(obj, prop, value);
    }
  });
  // </debug>
  /**
   * Create proxy for appOperation data if `ENABLE_DATA_PROXY` is true.
   */
  public proxyAppOperationData = () => {
    this.appOperationData = DataProxy.getDataProxy(this.appOperationData, this);
  };

  private constructor(initCfg: object) {
    // if (!_.get(initCfg, 'appDispatcherId')) {}
    if (!_.isEmpty(initCfg)) {
      _.assign(this, initCfg);
    } else {
      DispatcherManager.COUNTER = +localStorage.getItem('DEBUG_COUNTER');
      if (!this.appDispatchers[MAIN_DISPATCHER_ID] && this.appDispatcherId === MAIN_DISPATCHER_ID) {
        this.appDispatchers[MAIN_DISPATCHER_ID] = this;
        this.createAxios();
        this.createWebSocket();
      }
    }
    this.proxyAppOperationData();

    this.errorTrap = new ErrorTrap(this.logError);
  }

  /**
   * Encodes html text
   * @param {String} text
   */
  private encodeHTML = (text: string) => Esapi.encodeForHTML(text)

  /**
   * Decodes html text
   * Reverse from decodeHTML
   * @param {String} text
   */
  private decodeHTML = (text: string) => decodeHtmlCharacters(text);

  /**
   * Adds operations to existing action.
   * @param actionName Name of action in which will be appended operations.
   * @param operations Operations to append.
   * @param dispatcherId
   */
  public static addOperationsToExistingAction = (actionName: string, operations: Action, dispatcherId: string) => {
    let scope: DispatcherManager = Dispatcher?.appDispatchers[dispatcherId];
    if (scope) {
      let originalAction: Action = _.get(scope.appActions, actionName);
      if (!Array.isArray(originalAction)) {
        originalAction = [];
      }
      _.set(scope.appActions, actionName, originalAction.concat(...operations));
    }
  };

  /**
   * Log error to REST-API endpoint
   * @param {any} errorInfo Error info object
   */
  logError = (errorInfo: any): void => {
    let url = Configuration.get('REMOTE_LOG_URL');
    if (url) {
      this.executeAction('logError', Position.end, {
        params: errorInfo
      }, {
        // <debug>
        inserted: true
        // </debug>
      });
    }
  };
  // <debug> - used only in combination with dispatcher editor
  static addComplexOperationParentId = (o: any, complexOperationId: string) => {
    o.complexParentOperationId = complexOperationId; // used for dispatcher editor operation grouping
    if ([OperationType.conditional, OperationType.switch].includes(o.type)) {
      Object.keys(o).forEach((property: string) => {
        if (Array.isArray(o[property])) {
          o[property] = o[property].map((op: any) => DispatcherManager.addComplexOperationParentId(op, complexOperationId));
        }
      });
    }
    return o;
  };

  // </debug>
  /**
   * Add properties data as persistent.
   * @param {string[]} propertiesToAdd
   */
  private addPersistentData(propertiesToAdd: string[]): void {
    propertiesToAdd.map(this.mapPersistentDataAdd.bind(this));
  }

  /**
   * Cancel upload or download requests.
   * @param cancelRequestForFile Name of file to cancel upload/download. If cancelRequestForFile holds value `all`.
   * Then all files that are not uploaded/downloaded at the moment will be canceled.
   * @param cancelRequestForUploadGroup
   * @param skipBackToDispatcher
   */
  public cancelRequest(cancelRequestForFile: string = 'all', cancelRequestForUploadGroup: string = 'all', skipBackToDispatcher: boolean = false) {
    let groups: string[] = [cancelRequestForUploadGroup];
    let isCancelAll: boolean = cancelRequestForUploadGroup.toLowerCase() === 'all';

    if (isCancelAll) {
      groups = Object.keys(DispatcherManager.UPLOAD);
    }

    groups.forEach((uploadGroup: string) => {
      if (DispatcherManager.UPLOAD[uploadGroup]) {
        if (cancelRequestForFile.toLowerCase() === 'all') {
          for (let fileName in DispatcherManager.UPLOAD[uploadGroup].DATA) {
            DispatcherManager.cancelSingleRequest(uploadGroup, fileName, isCancelAll);
          }
        } else {
          DispatcherManager.cancelSingleRequest(uploadGroup, cancelRequestForFile, isCancelAll);
        }
      }
    });

    if (!skipBackToDispatcher) {
      this.updateUploadProgresses(this.appDispatcherId, '');
      this.backToDispatcher();
    }
  }

  /**
   * Cancel upload request if file is not uploaded completely.
   * @param uploadGroupId
   * @param fileName
   * @param isCancelAll
   */
  private static cancelSingleRequest(uploadGroupId: string, fileName: string, isCancelAll: boolean) {
    const percentage: number = DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName];
    if (percentage < 100) {
      let errorCode: string = DISPATCHER_UPLOAD_CANCELED;
      if (isCancelAll) {
        errorCode = DISPATCHER_ALL_UPLOAD_CANCELED;
        Object.keys(DispatcherManager.UPLOAD[uploadGroupId].DATA).forEach((fileName: string) => {
          if (DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] === 0) {
            DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] = UPLOAD_REQUEST_CANCELED;
          }
        });
      }
      DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] = UPLOAD_REQUEST_CANCELED;
      if (DispatcherManager.UPLOAD[uploadGroupId].CANCEL[fileName]) {
        DispatcherManager.UPLOAD[uploadGroupId].CANCEL[fileName](errorCode);
      } else {
        DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] = UPLOAD_REQUEST_CANCEL_SCHEDULED;
      }
      delete DispatcherManager.UPLOAD[uploadGroupId].CANCEL[fileName];
    }
  }

  /**
   * Clean up request data from proper dispatcher after request is finished.
   * @param dispatcherId Dispatcher id can be different then current appDispatcherId if operation is asynchronous
   * request with isUpload property set to true. That is why we pass dispatcher id to ensure that data is cleared in
   * correct dispatcher.
   */
  private cleanUpRequestData = (dispatcherId: string) => {
    const dispatcher = this.appDispatchers[dispatcherId];
    if (dispatcher) {
      const length = DispatcherManager.PROPERTIES_TO_REMOVE_AFTER_EVERY_REQUEST.length;
      for (let i = 0; i < length; i++) {
        dispatcher.appOperationData[DispatcherManager.PROPERTIES_TO_REMOVE_AFTER_EVERY_REQUEST[i]] = undefined;
      }
    }
  };

  /**
   * Executes synchronous or asynchronous `Promise` based on passed `operation` configuration.
   *
   * @param {PromiseConfig} config
   */
  private async doPromise(config: PromiseConfig) {
    const { arg, operation, module, operationItem } = config;
    const { fn } = operationItem;
    let { debug = false, isCallback = true, mappingName = fn, synchronous = true, persistent } = operation;
    this.stopExecOperations = this.stopExecOperations && synchronous;
    let method = module[fn];
    let result;

    // <debug>
    if (debug) {
      let debugInfo = DispatcherManager.debugModeInfo(operation);
      let { fnBody, argName } = DispatcherManager.functionWithDebugger(method, fn);
      fnBody = debugInfo + fnBody;
      // eslint-disable-next-line no-new-func
      method = Function(argName, fnBody).bind(module);
    } // </debug>

    const executeMethod = isCallback ? this.doPromiseWithCallback : method;
    const scope = isCallback ? this : module;
    const args = isCallback ? [method, arg] : _.isArray(arg) ? arg : [arg];

    if (synchronous) {
      result = await executeMethod.apply(scope, args).catch((e: any) => {
        this.handlePromiseError(e, mappingName, operation);
      });
      if (result) {
        if (!_.isNull(this.aggregatedResults) && _.isArray(this.aggregatedResults)) {
          this.aggregatedResults.push(result);
          this.appOperationData[mappingName] = this.aggregatedResults.slice();
        } else {
          this.appOperationData[mappingName] = result;
        }
        // this.appOperationData[mappingName] = result;
      }
    } else {
      if (mappingName === fn) {
        mappingName = DispatcherManager.RESPONSE_PROMISE_DEFAULT_NAME;
      }
      // <debug>
      if (typeof operation.persistent !== 'boolean') {
        throw new OperationError('Asynchronous requests must have "persistent" property set to `true`!', operation);
      }
      if (typeof operation.mappingName === 'undefined') {
        throw new OperationError('Asynchronous requests must have "mappingName" property!', operation);
      }
      if (DispatcherManager.RESPONSE_PROMISE_DEFAULT_NAME === mappingName) {
        throw new OperationError(`Asynchronous requests must have "mappingName" different from \`${DispatcherManager.RESPONSE_PROMISE_DEFAULT_NAME}\`!`, operation);
      }
      // </debug>

      executeMethod
        .apply(scope, args)
        .then((response: any) => {
          if (response) {
            this.appOperationData[mappingName] = response;
            this.backToDispatcher(operation.subName);
          }
        })
        .catch((reason: any) => {
          this.handlePromiseError(reason, mappingName, operation);
        });
    }
    if (persistent) {
      this.manipulateWithPersistentData(OperationType.addPersistentData, [mappingName]);
    }
    this.stopExecOperations = false;
    this.backToDispatcher();
  }

  /**
   * Returns new `Promise` with passed `callback`.
   *
   * @param {Function} callback
   * @param {any} args
   */
  private doPromiseWithCallback = (callback: Function, args: any) => {
    if (Array.isArray(args)) {
      // <debug>
      if (args.length && callback.length < 2) {
        throw new Error('Promise callback should have first two mandatory arguments resolve and reject!');
      }
      // </debug>
      callback = attach(null, callback, args, true);
    }

    return new Promise(callback as any);
  };

  /**
   * Creates close operation based on passed parameters.
   * @param moduleName
   * @param pos
   * @param actionName
   */
  private getCloseOperation(moduleName: string, pos: DialogPosition | number, actionName: string): Operation {
    let operation: Operation = {
      actionName,
      internalOperation: true,
      moduleName,
      name: 'hide',
      synchronous: true
    };
    if (!_.isBoolean(pos)) {
      _.assign(operation, {
        args: ['cfg'],
        data: {
          cfg: {
            pos,
            dispatcherId: this.appDispatcherId
          }
        }
      });
    }
    return operation;
  }

  /**
   * Closing warning dialog and/or dialog via dispatcher actions.
   * @param operation
   */
  private handleCloseOperation(operation: CloseOperation) {
    let { actionName, index = DialogPosition.last } = operation;
    let windowType = _.get(operation, 'windowType', WindowType.any);
    let operations: Action = [];
    let data: any = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    // <debug>
    let complexOperationId = actionName + '_' + uuid();
    this.showInDispatcherEditor({
      ...operation,
      complexOperationId
    }, data);
    // </debug>

    switch (windowType) {
      case WindowType.warning:
        operations.push(this.getCloseOperation('WarningDialog', index, actionName));
        break;
      case WindowType.dialog:
        operations.push(this.getCloseOperation('Dialog', index, actionName));
        break;
      default: // WindowType.any
        if (index === DialogPosition.all) {
          operations.push(
            this.getCloseOperation('WarningDialog', index, actionName),
            this.getCloseOperation('Dialog', index, actionName)
          );
        } else {
          let isWarningActive: boolean = DispatcherWindowManager.isActiveWindowWarning();
          let isDialogActive: boolean = DispatcherWindowManager.isActiveWindowDialog();
          if (isWarningActive) {
            operations.push(this.getCloseOperation('WarningDialog', index, actionName));
          }
          if (isDialogActive) {
            operations.push(this.getCloseOperation('Dialog', index, actionName));
          }
        }
        break;
    }

    operations.push({
      actionName,
      internalOperation: true,
      name: 'setFocusToActiveDialog',
      type: OperationType.action
    });

    // <debug> - used only in combination with dispatcher editor
    operations = operations.map((op: any) => DispatcherManager.addComplexOperationParentId(op, complexOperationId));
    // </debug>

    spliceArray(this.appExecutedOperations, operations, 1, 0);
    this.backToDispatcher();
  }

  /**
   * Handles adding, removing and getting of application cookies.
   * @param operation
   */
  private handleCookieOperation(operation: CookieOperation) {
    const { actionName, args, method } = operation;
    let name: string;
    let value: any;
    let options: any;

    let data: any = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    // <debug>
    this.showInDispatcherEditor({
      ...operation
    }, data);
    // </debug>

    switch (method) {
      case CookieMethod.checkConsent:
        let operations: Action = [{
          actionName,
          internalOperation: true,
          name: 'checkCookieConsent',
          type: OperationType.action
        }];
        spliceArray(this.appExecutedOperations, operations, 1, 0);
        break;
      case CookieMethod.consentUrl:
        let [consentUrl] = args;
        CookieUtil.setConsentUrl(this.appOperationData[consentUrl]);
        break;
      case CookieMethod.defaultDuration:
        let [defaultDuration] = args;
        let duration = this.appOperationData[defaultDuration];
        // <debug>
        if (!_.isNumber(duration)) {
          throw new OperationError('Default cookie duration has to be number!', operation);
        }
        // <debug>
        CookieUtil.setDefaultDuration(duration);
        break;
      case CookieMethod.getCookie:
        [name, options] = args;
        let result: any = CookieUtil.get(this.appOperationData[name], this.appOperationData[options]);
        let getByName: string = _.isString(operation.mappingName) ? operation.mappingName : name;
        this.appOperationData[getByName] = result;
        break;
      case CookieMethod.removeCookie:
        [name, options] = args;
        CookieUtil.remove(this.appOperationData[name], this.appOperationData[options]);
        break;
      case CookieMethod.setCookie:
        [name, value, options] = args;
        let { cookieType } = operation;
        this.setCookie(name, value, options, cookieType);
        break;
      default:
        break;
    }
    this.backToDispatcher();
  }

  /**
   * Sets cookies by type
   * @param {string} name
   * @param {string} value
   * @param {any} options
   * @param {string} cookieType
   */
  private setCookie = (name, value, options, cookieType) => {
    if (cookieType === ConsentCookies.analytics) {
      CookieUtil.setAnalyticsCookie(this.appOperationData[name], this.appOperationData[value], this.appOperationData[options]);
    } else if (cookieType === ConsentCookies.functional) {
      CookieUtil.setFunctionalCookie(this.appOperationData[name], this.appOperationData[value], this.appOperationData[options]);
    } else if (cookieType === ConsentCookies.required) {
      CookieUtil.setRequiredCookie(this.appOperationData[name], this.appOperationData[value], this.appOperationData[options]);
    } else {
      CookieUtil.set(this.appOperationData[name], this.appOperationData[value], this.appOperationData[options]);
    }
  }
  /**
   * Logs `Promise` error.
   *
   * @param {any} e
   */
  handlePromiseCatch = (e: any) => {
    this.errorTrap.trap(e);
  };

  /**
   * Handles `Promise` error.
   *
   * @param {any} reason
   * @param {string} mappingName
   * @param {PromiseOperation} operation
   */
  private handlePromiseError = (reason: any, mappingName: string, operation: PromiseOperation) => {
    const { synchronous = true } = operation;

    this.errorTrap.trap(reason);

    if (operation.handleError) {
      this.appOperationData[mappingName] = reason;
      // <debug>
      messageWithColors(`%cPromise has been rejected with error: ${JSON.stringify(reason)}%c`, 'purple');
      // </debug>
      if (!synchronous) {
        this.backToDispatcher(operation.subName, false, false);
      }
    } else {
      this.clearAllOperationData();
      this.backToDispatcher(false, true);
      // <debug>
      // TODO server log
      throw new OperationError(`Promise has been rejected with error: ${JSON.stringify(reason)}`, operation);
      // </debug>
    }
  };

  /**
   * Handles language operation type.
   * @param operation
   */
  private handleLanguageOperation(operation: LanguageOperation) {
    const { actionName, change = false } = operation;
    let operations: Action = [];

    let data: any = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    const dispatcherEditorAdditionalData: any = {
      // <debug>
      actionName,
      internalOperation: true
      // </debug>
    };
    if (change) {
      let { locale } = this.appOperationData;
      // <debug>
      if (!_.isString(locale)) {
        throw new OperationError('locale is missing...', operation);
      }
      // </debug>

      operations.push({
        ...dispatcherEditorAdditionalData,
        data: {
          locale
        },
        name: 'changeLanguage',
        type: OperationType.action
      });
    }
    // eslint-disable-next-line array-bracket-newline
    spliceArray(this.appExecutedOperations, [
      // <debug>
      {
        ...operation,
        actionName,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        editorData: data
      },
      // eslint-disable-next-line array-element-newline
      // </debug>
      ...operations], 1, 0);
    this.backToDispatcher();
  }

  /**
   * Appends additional depedencies to operations within MergeAsyncRequest operation type.
   * @param operations
   * @param o Current operation
   * @param subName
   * @param mergeRequest Unique id. Used to link same cancellation token for all requests within operations.
   */
  private appendMergeAsyncRequestDependence = (operations: any[], o: any, subName: string | string[], mergeRequest: any) => {
    if (!Array.isArray(o.dependence)) {
      let dep = o.dependence;
      o.dependence = [];
      if (dep) {
        o.dependence.push(dep);
      }
    }
    if (Array.isArray(subName)) {
      o.dependence.unshift(...subName);
    } else {
      o.dependence.unshift(subName);
    }

    if (o.subName && o.type === OperationType.request) {
      o.partOfMergeRequest = mergeRequest;
      if (!o.synchronous) {
        this.mappedSubNames.push(o.subName);
      }
    }
    operations.push(o);

    if ([OperationType.action, OperationType.stack].includes(o.type)) {
      operations.pop();
      let action = _.cloneDeep(this.appActions[o.name]);
      let hasAsyncRequest: any = _.find(action, {
        // synchronous: false,
        type: OperationType.request
      });
      if (hasAsyncRequest) {
        hasAsyncRequest.dependence = subName;
        hasAsyncRequest.partOfMergeRequest = mergeRequest; // in order to cancel
        this.mappedSubNames.push(hasAsyncRequest.subName);
      }
      operations.push(...action);
    }
    return operations;
  };

  /**
   * Handles MergeAsyncRequest operation type.
   * @param operation
   */
  private handleMergeAsyncRequestOperation(operation: MergeAsyncRequest) {
    let {
      actionName,
      unique = uuid(),
      operations = [],
      mappingName,
      data,
      persistent,
      synchronous = false,
      handleError = false
    } = operation;

    if (!synchronous) {
      if (!mappingName) {
        mappingName = DispatcherManager.MERGE_ASYNC_RESPONSE_OBJECT_DEFAULT_NAME;
      }
      if (_.isUndefined(persistent)) {
        persistent = true;
      }
    }

    if (unique.includes('{') && unique.includes('}')) {
      unique = this.getTemplateExpression(unique, true);
    }
    let dispatcherId: string = unique;
    let realData: any = this.handleTemplatingAndUpdatingData(data);
    let mergeRequest: string = unique;
    let newlyCreatedActionName: string = DispatcherManager.MERGE_ASYNC_REQUEST_PREFIX + unique;

    // <debug>
    this.showInDispatcherEditor({
      ...operation,
      name: newlyCreatedActionName
    }, realData);
    // </debug>
    let { params = {}, url, method = undefined } = this.appOperationData;

    // <debug>
    if (!_.isString(url)) {
      throw new OperationError('url must be provided', operation);
    }
    if (_.isEmpty(params)) {
      throw new OperationError('params must be provided', operation);
    }
    // </debug>

    let syncRequest: any = {};
    let dependentOperation: any = {};
    if (!synchronous) {
      let subName: string = unique + 'subName';
      operations = operations.reduce((arr: Operation[], o: any) => this.appendMergeAsyncRequestDependence(arr, o, subName, mergeRequest), []);
      syncRequest.subName = subName;
      dependentOperation.dependence = subName;
      if (this.mappedSubNames.length) {
        dependentOperation.dependence = [subName, ...this.mappedSubNames];
        this.mappedSubNames = [];
      }
      operations.unshift({
        data: {
          template: {
            bind: {
              [DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME]: `{${mappingName}}`
            },
            params: undefined // clean up previous params
          }
        },
        ...dependentOperation,
        type: OperationType.data
      });
    }
    if (_.isObject(realData)) {
      // @ts-ignore
      delete realData.params;
    }
    let items: string[] = [...Object.keys(params), ...Object.keys(realData || {})];
    const dispatcherEditorAdditionalData: any = {
      // <debug>
      internalOperation: true
      // </debug>
    };
    let executeOperations: Action = [{
      ...dispatcherEditorAdditionalData,
      items,
      dispatcherIds: [dispatcherId],
      referenceDispatcherId: this.appDispatcherId,
      type: OperationType.syncDispatchersDataItem
    }, {
      ...dispatcherEditorAdditionalData,
      persistent: items,
      type: OperationType.addPersistentData
    }, {
      ...dispatcherEditorAdditionalData,
      data: {
        params
      },
      type: OperationType.data
    }, {
      ...dispatcherEditorAdditionalData,
      data: {
        url,
        method
      },
      mappingName,
      mergeRequest,
      handleError,
      persistent,
      ...syncRequest,
      synchronous,
      type: OperationType.request
    }, ...operations, {
      ...dispatcherEditorAdditionalData,
      ...dependentOperation,
      independent: true,
      dispatcherId: this.appDispatcherId,
      name: newlyCreatedActionName,
      type: OperationType.cleanUpAction
      // action is initially added to the dispatcher which called action and than
      // that action is copied to new dispatcher, when destroying newly created dispatcher we should clean up action
      // from dispatcher which created action
    }, {
      ...dispatcherEditorAdditionalData,
      ...dependentOperation,
      independent: true,
      dispatcherIds: [dispatcherId],
      type: OperationType.unlink
    }];

    _.set(this.appActions, newlyCreatedActionName, executeOperations);

    // TODO add log to dispatcher editor

    spliceArray(this.appExecutedOperations, [{
      actionName,
      actionType: {
        dispatcherId
      },
      name: newlyCreatedActionName,
      type: OperationType.action
    }], 1, 0);

    this.backToDispatcher();
  }

  /**
   * Handle sign in and sign out operations.
   * @param operation
   */
  private handleSignOperation(operation: SignOperation) {
    const { actionName, subType, type } = operation;
    let operations: Action = [];
    let name: string;
    let data: any = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));

    const dispatcherEditorAdditionalData: any = {
      // <debug>
      actionName,
      internalOperation: true
      // </debug>
    };

    switch (type) {
      case OperationType.signIn:
        if (subType === SignType.init) {
          name = 'loginRequest';
        } else if (subType === SignType.request) {
          name = 'signInRequest';
        }
        break;
      case OperationType.signOut:
        if (subType === SignType.init) {
          name = 'signOutWarning';
        } else if (subType === SignType.request) {
          name = 'signOutRequest';
        }
        break;
      default:
        break;
    }

    if (name) {
      operations.push({
        ...dispatcherEditorAdditionalData,
        name,
        type: OperationType.action
      });
    }

    // eslint-disable-next-line array-bracket-newline
    spliceArray(this.appExecutedOperations, [
      // <debug>
      {
        ...operation,
        actionName,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        editorData: data
      },
      // eslint-disable-next-line array-element-newline
      // </debug>
      ...operations], 1, 0);
    this.backToDispatcher();
  }

  /**
   * Handles theme operation type.
   * @param operation
   */
  private handleThemeOperation(operation: ThemeOperation) {
    const { actionName, load } = operation;
    let operations: Action = [];

    let data: any = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    const dispatcherEditorAdditionalData: any = {
      // <debug>
      actionName,
      internalOperation: true
      // </debug>
    };

    if (load === ThemeLoad.change) {
      operations.push({
        ...dispatcherEditorAdditionalData,
        name: 'changeTheme',
        type: OperationType.action
      });
    } else if (load === ThemeLoad.init) {
      operations.push({
        ...dispatcherEditorAdditionalData,
        name: 'loadThemes',
        type: OperationType.action
      });
    }

    // eslint-disable-next-line array-bracket-newline
    spliceArray(this.appExecutedOperations, [
      // <debug>
      {
        ...operation,
        actionName,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        editorData: data
      },
      // eslint-disable-next-line array-element-newline
      // </debug>
      ...operations], 1, 0);
    this.backToDispatcher();
  }

  /**
   * Handles redirect operation type.
   * @param operation
   */
  private handleRedirectOperation(operation: RedirectOperation) {
    const { consent = false, signIn = false, actionName } = operation;
    let operations: Action = [];

    let data: any = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    // <debug>
    this.showInDispatcherEditor({
      ...operation
    }, data);
    // </debug>
    const dispatcherEditorAdditionalData: any = {
      // <debug>
      actionName,
      internalOperation: true
      // </debug>
    };
    let { path } = this.appOperationData;
    if (consent) {
      this.appOperationData.cookieConsent = consent;
      this.redirectTo(path, this.appOperationData.forceRefresh, this.appOperationData.cookieConsent);
    } else if (signIn) {
      operations.push({
        data: {
          path
        },
        ...dispatcherEditorAdditionalData,
        name: 'redirectToSignIn',
        type: OperationType.action
      });
      spliceArray(this.appExecutedOperations, operations, 1, 0);
    } else {
      this.redirectTo(path, this.appOperationData.forceRefresh, this.appOperationData.cookieConsent);
    }

    this.backToDispatcher();
  }

  /**
   * Initialize all upload files with progresses of 0 before actual upload is started.
   * @param uploadGroupId
   * @param fileNames
   */
  private initializeUploadProgressDataPerGroupUpload(uploadGroupId: string, fileNames: string[] = []) {
    if (!DispatcherManager.UPLOAD[uploadGroupId]) {
      DispatcherManager.UPLOAD[uploadGroupId] = {
        CANCEL: {},
        DATA: {}
      };
    }
    fileNames.forEach((fileName: string) => {
      DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] = 0;
    });
  }

  /**
   * Register all actions to dispatcher.
   * @param {ActionsObj} actionsObj
   * @param {string} moduleName
   */
  public registerActions(actionsObj: ActionsObj, moduleName: string): void {
    // <debug>
    if (!this.actionsLoaded) {
      let actionNames: string[] = Object.keys(actionsObj);
      let duplicatedActions = _.intersection(Object.keys(this.appActions), actionNames);
      if (duplicatedActions.length) {
        throw new Error(`Action name can not be overridden "${duplicatedActions}"! Please choose different name for actions.`);
      }
      if (_.get(actionsObj, DispatcherManager.GENERIC_ACTION_NAME)) {
        throw new Error(`Action name can't be "${DispatcherManager.GENERIC_ACTION_NAME}"! Please choose different name for action.`);
      }
      let forbiddenNames: string[] = actionNames.filter((name: string) => name.match(/^(WarningDialog|Dialog|Widget|DownloadWidget)_(\d+)_(\d+)$/));
      if (forbiddenNames.length) {
        throw new Error(`Forbidden names: ${forbiddenNames.join(', ')}/\n Please rename those actions.`);
      }
    }
    // </debug>

    if (!_.isUndefined(actionsObj) && !this.actionsLoaded) {
      _.merge(this.appActions, _.clone(actionsObj));
      this.actionsLoaded = true;
    }
    // <debug>
    else {
      if (!this.actionsLoaded) {
        throw new Error('Currently is not supported to apply actions to multiple modules!\nOnly one module should have "actions" props and by default is should be top level dispatcher component such as "App".\nPlease remove "actions" from <' + moduleName + ' actions={...} />');
      }
    }
    // </debug>
  }

  /**
   * Register module component to dispatcher.
   * @param {string} moduleName
   * @param {React.Component} module
   */
  public registerModule(moduleName: string, module: any): void {
    if (!_.get(this.appModules, moduleName)) {
      this.appModules[moduleName] = [];
    }
    this.appModules[moduleName].push(module);
  }

  /**
   * Register module component as mounted. If there is operation that wait module to be mounted, operation will be executed.
   * @param {string} moduleName
   */
  public registerModuleAsMounted(moduleName: string): void {
    if (this.appWaitingForModule && this.appWaitingForModule === moduleName) {
      this.executeCurrentOperation();
    }
  }

  /**
   * Registers listener for some dispatcher variables
   * @param {String} moduleName
   * @param {Object[]} updateStates Listener array
   */
  public registerUpdateStateModule = (moduleName: string, updateStates: { [key: string]: UpdateStateModule }) => {
    Object.keys(updateStates).forEach((prop, index) => {
      if (!this.updateStateModules[prop]) {
        this.updateStateModules[prop] = [];
      }
      this.updateStateModules[prop].push({
        ...updateStates[index],
        moduleName
      });
    });
  };

  /**
   * Unregister module component to dispatcher.
   * @param {string} moduleName
   * @param {React.Component} module
   */
  public unregisterModule(moduleName: string, module: any): void {
    _.remove(this.appModules[moduleName], module);
  }

  /**
   * Unregisters listener for some dispatcher variables
   * @param {String} moduleName
   */
  public unregisterUpdateStateModule = (moduleName: string) => {
    Object.keys(this.updateStateModules).forEach((prop) => {
      this.updateStateModules[prop] = this.updateStateModules[prop].filter((item: any) => item.moduleName !== moduleName);
    });
  };

  /**
   * Removes executed operation from executed operation list and set next operation(action) to be executed.
   * @param {boolean/string} dependence Dependent action name. If is not empty, dependent actions with this action name will be executed
   * @param {boolean/string} deleteActionGroup Action name of operations that need to be deleted
   * @param {boolean} deleteAllOperations If true all current  operations will be deleted
   */
  private backToDispatcher(dependence?: boolean | string, deleteActionGroup?: boolean | string, deleteAllOperations?: boolean): void {
    if (dependence) {
      this.deleteDependentOperations(dependence);
      this.executeAction(DispatcherManager.GENERIC_ACTION_NAME, {
        pos: Position.end,
        dispatcherId: this.appDispatcherId
      }, undefined, {
        // <debug>
        inserted: true
        // </debug>
      });
    } else if (!this.stopExecOperations || deleteActionGroup || deleteAllOperations) {
      this.deleteOperations(this.appExecutedOperations, 0, deleteActionGroup, deleteAllOperations);
      this.executeCurrentOperation(true);
    }
  }

  /**
   * Clears all operation data except persistent data.
   */
  private clearAllOperationData(): void {
    let recentDataProperties: any = {};
    for (const prop in this.appAllPersistentData) {
      if (_.has(this.appAllPersistentData, prop)) {
        recentDataProperties[prop] = this.appOperationData[prop];
      }
    }
    this.appOperationData = recentDataProperties;
    this.proxyAppOperationData(); // recreates proxy
  }

  /**
   * Convert items configuration from operation types OperationType.warning, OperationType.dialog and OperationType.widget to valid operations.
   * @param {string} moduleName
   * @param {ButtonActionConfig[]} items
   * @param {Operation[]} beforeOperations
   */
  private convertItemsConfigIntoActions(moduleName: string, items: ButtonActionConfig[] = [], beforeOperations?: Action): DialogButton[] {
    let buttons: DialogButton[] = [];
    let operations;
    let numberOfOpenedWindowType = _.get(DispatcherWindowManager.openedMultiWindows, moduleName);
    if (!numberOfOpenedWindowType) {
      numberOfOpenedWindowType = 0;
    }
    let itemOperations;

    items.forEach((item: ButtonActionConfig, i: number) => {
      itemOperations = item.operations || [];
      let actionName = `${moduleName}_${numberOfOpenedWindowType}_${i}`;
      let { close = true } = item;
      if (close) {
        if (item.runningOperationsAfterClose) {
          operations = _.concat(beforeOperations as Operation, itemOperations);
        } else {
          operations = _.concat(itemOperations, beforeOperations as Operation);
        }
      } else {
        operations = itemOperations;
      }
      _.set(this.appActions, actionName, operations);

      let { placeholders = {} } = item;
      let button: DialogButton = {
        placeholders: this.handleTemplatingAndUpdatingData(placeholders),
        actionName,
        name: item.btnName,
        close
      };

      if (item.btnConfig) {
        const btnConfig = this.handleTemplatingAndUpdatingData(item.btnConfig);
        if (btnConfig.args) {
          btnConfig.args = this.getOperationArg(btnConfig.args, null, true);
        }
        button.btnConfig = _.merge(
          {
            className: undefined,
            fillMode: undefined,
            pos: Position.start,
            themeColor: null,
            iconClass: undefined
          },
          btnConfig,
          {
            hidden: !!btnConfig.hidden
          }
        );
      }
      buttons.push(button);
    });
    return buttons;
  }

  private static modifyCloseWidgetOperation(operation: Operation, operations: Action, index: number, actionName: string): void {
    const { moduleName } = operation;
    if (['Widget', 'DownloadWidget'].indexOf(moduleName) > -1) {
      let apply = {
        executeType: 'widget'
      };
      // <debug>
      if (operation.debug) {
        _.set(apply, 'debug', operation.debug);
      }
      if (operation.log) {
        _.set(apply, 'log', operation.log);
      }
      // </debug>
      if (operation.dependence) {
        _.set(apply, 'dependence', operation.dependence);
      }
      if (operation.independent) {
        _.set(apply, 'independent', operation.independent);
      }
      const hasModuleId = _.get(operation, 'data.moduleId') || _.get(operation, 'data.template.moduleId') || _.get(operation, 'data.template.bind.moduleId');
      _.assign(operation, {
        ...apply,
        args: operation.args || ['moduleId', 'activeDownloadCount']
      });

      if (!hasModuleId) {
        _.assign(operation, {
          internalOperation: true,
          data: {
            template: {
              bind: {
                moduleId: '{moduleId}'
              }
            }
          }
        });
      }

      switch (operation.name) {
        case 'close':
          spliceArray(operations, [{
            actionName,
            data: {
              template: {
                bind: {
                  activeDownloadCount: '{numberOfActiveDownloads()}'
                }
              }
            },
            internalOperation: true,
            type: OperationType.data
          }, {
            actionName,
            ...apply,
            evaluate: true,
            name: `"close${moduleName}_" + {moduleId}`,
            moduleName: '{moduleId}',
            internalOperation: true,
            type: OperationType.cleanUpAction
          }, {
            actionName,
            ...apply,
            persistent: ['moduleId'],
            internalOperation: true,
            type: OperationType.removePersistentData
          }], index);
          break;
        case OperationType.maskModule:
        case OperationType.unmaskModule:
          break;
      }
    }
  }

  /**
   * Adds actionName property to action group operations and separates dependence operation.
   * @param {Operation[]} operations
   * @param {string} actionName
   * @param {ActionType/Position} actionType
   * @returns {Action}
   */
  private createOperationProperties(operations: Operation[], actionName: string, actionType?: ActionType | Position): Action {
    let operationsLength = operations.length;
    let operation;
    const propagationIndependent = _.get(actionType, 'propagationIndependent');
    const isPropagationIndependentBoolean = _.isBoolean(propagationIndependent);
    let removePersistentData = null;
    let dependenceName;

    let startIndex: number = this.appDependenceActions.length;

    for (let i = 0; i < operationsLength; i++) {
      operation = operations[i] as Operation;
      DispatcherManager.modifyCloseWidgetOperation(operation, operations, i, actionName);
      operation.actionName = actionName;

      if (_.includes([OperationType.action, OperationType.stack], operation.type) && !_.get(operation, 'actionType.dispatcherId')) {
        if (operation.actionType) {
          operation.actionType.dispatcherId = this.appDispatcherId;
        } else {
          operation.actionType = {
            dispatcherId: this.appDispatcherId
          };
        }
      }
      if (isPropagationIndependentBoolean) {
        operation.independent = propagationIndependent;
      }

      if (operation.subName) {
        // add removing persistent data when actions are dependent
        removePersistentData = operation.hasOwnProperty('mergeRequest') ? removePersistentData : operation.mappingName;
        dependenceName = operation.subName;
      }

      if (operation.dependence) {
        if (Array.isArray(operation.dependence) && operation.dependence.length === 1) {
          operation.dependence = operation.dependence[0];
        }

        this.appDependenceActions.splice(startIndex, 0, _.cloneDeep(operation));
        startIndex++;
        operation.remove = true;
      }
    }
    operations = operations.filter((op: any) => !op.remove);

    if (removePersistentData) {
      let skip: any = _.find(this.appDependenceActions, {
        dependence: dependenceName,
        type: OperationType.unlink
      });

      if (!skip) {
        // no need for removingPersistentData when dispatcher will be destroyed before that
        this.appDependenceActions.push({
          dependence: dependenceName,
          persistent: [removePersistentData],
          type: OperationType.removePersistentData
        });
      }
    }

    const loop: LoopObjectType = _.get(actionType, 'loop');
    if (loop) {
      let counter = loop.counter;
      if (_.isFinite(counter)) {
        let increment = loop.increment;
        if (!increment) {
          increment = 1;
          loop.increment = increment;
        }
        counter += increment;
        loop.counter = counter;
      }
      const loopVariables = loop.variables;
      if (loopVariables) {
        for (const prop in loopVariables) {
          if (_.has(loopVariables, prop)) {
            (actionType as any)[prop] = this.getTemplateExpression(loopVariables[prop], true);
          }
        }
      }

      if (Array.isArray(loop.breakOperations)) {
        loop.breakOperations = loop.breakOperations.map((op) => {
          op.dependence = dependenceName;
          return op;
        });
      }
      (actionType as any).loop = loop;
      operations.push({
        actionType: actionType,
        name: actionName,
        type: OperationType.stack
      } as StackOperation);
    }
    const executeOnlyOperationType = _.get(actionType, 'executeOnlyOperationType');
    if (executeOnlyOperationType) {
      operations = this.filterOperationsByType(operations, executeOnlyOperationType);
    }
    return operations;
  }

  private filterOperationsByType = (operations: Operation[], type: OperationType) => {
    operations = operations.filter((item: Operation) => item.type === type);
    return operations;
  };

  /**
   * Function which will be executed only in development mode. Enables debugging dispatchers actions.
   * @param {any} obj
   * @return {any}
   */
  private static debugMode(obj: any): any {
    let { argName, fnBody, operation, operationFn, module } = obj;
    // <debug>
    let operationDebugger = operation.debug;
    let data;
    // </debug>
    let debugExpression = '';
    let info = DispatcherManager.debugModeInfo(operation);

    if (fnBody.match(/\)\(arg\);/)) {
      fnBody = `return this.${operationFn};`;
    }

    if (module) {
      let fnParts = module[operationFn];
      if (fnParts) {
        // <debug>
        if (operationDebugger) {
          data = DispatcherManager.functionWithDebugger(fnParts, operationFn);
          fnBody = data.fnBody;
          argName = data.argName;
        }
        // </debug>
      } else {
        fnParts = operationFn.split('.');
        let scope = module;
        let length = fnParts.length;
        let item = 0;
        let part;

        while (item < length) {
          part = fnParts[item];
          if (part.match(/\)/)) {
            scope = scope[part.split('(')[0]]();
            fnBody = fnBody.replace('.' + part, '');
          } else {
            if (_.isFunction(scope[part])) {
              // <debug>
              if (operationDebugger) {
                fnParts = scope[part].toString();
                data = DispatcherManager.functionWithDebugger(fnParts, operationFn);
                fnBody = data.fnBody;
                argName = data.argName;
              }
              // </debug>
            } else {
              if (item + 1 === length) {
                if (!_.isUndefined(scope[fnBody.replace('(arg)', '')])) {
                  fnBody = fnBody.replace('(arg)', '');
                }
              } else {
                fnBody = fnBody.replace('.' + part, '');
                scope = scope[part];
              }
            }
            module = scope;
          }
          item++;
        }
      }
    }

    argName = argName.replace(/\s+/g, '').split(',');
    if (argName.length === 1) {
      argName = argName[0];
    }

    return {
      argName: operation.args || argName,
      fnBody: `${info}${debugExpression}${fnBody}`,
      module: module
    };
  }

  /**
   * Returns description of operation in debugging mode.
   * @param {Operation} operation
   * @returns {string}
   */
  private static debugModeInfo(operation: Operation): string {
    let info = '';
    // <debug>
    const operationDebugger = operation.debug;
    if (operationDebugger) {
      let debugExpression = _.isString(operationDebugger) ? `//Debug Comment: ${operationDebugger}\n` : '';
      _.unset(operation, 'debug');
      info = `${DispatcherManager.formatOperationToString(operation)}${debugExpression}${DispatcherManager.DEBUG_EXPRESSION}`;
    }
    // </debug>
    return info;
  }

  /**
   * Push every dependent operation to generic action list, and delete that operation from dependence action list.
   *
   * @param {boolean/string} dependence Dependent action name. If is not empty, dependent actions with this action name will be executed
   */
  private deleteDependentOperations(dependence: boolean | string): void {
    this.appActions[DispatcherManager.GENERIC_ACTION_NAME] = [];
    let dependenceLength = this.appDependenceActions.length;
    let dependenceCloneOperation;
    let operation: any;
    let addOperationToGenericAction: boolean;
    while (dependenceLength) {
      dependenceLength--;
      addOperationToGenericAction = false;
      operation = this.appDependenceActions[dependenceLength];
      if (operation && operation.dependence) {
        if (Array.isArray(operation.dependence) && _.isString(dependence) && operation.dependence.includes(dependence)) {
          let index: number = operation.dependence.indexOf(dependence);
          spliceArray(operation.dependence as string[], [], index, 1);
          if (operation.dependence.length === 1) {
            operation.dependence = operation.dependence[0];
          }
          addOperationToGenericAction = true;
        } else if (operation.dependence === dependence) {
          addOperationToGenericAction = true;
        }

        if (addOperationToGenericAction) {
          dependenceCloneOperation = _.cloneDeep(operation);
          if (dependenceCloneOperation.dependence === dependence) {
            _.unset(dependenceCloneOperation, 'dependence');
          }
          (this.appActions[DispatcherManager.GENERIC_ACTION_NAME] as Action).unshift(dependenceCloneOperation);
          // unshift in order to keep proper operation order
          this.deleteOperations(this.appDependenceActions, dependenceLength);
        }
      }
    }
  }

  /**
   * Deletes operations from list
   * @param {Operation[]} deleteArray Operation list
   * @param {number} index Deleted operation index
   * @param {string/boolean} deleteActionGroup Action name of operations that need to be deleted
   * @param {boolean} deleteAllOperations If true all current  operations will be deleted
   */
  private deleteOperations(deleteArray: Operation[], index: number, deleteActionGroup?: string | boolean, deleteAllOperations?: boolean): void {
    if (deleteAllOperations) {
      this.appExecutedOperations = deleteArray.filter(DispatcherManager.getIndependentOperation, this);
    } else if (deleteActionGroup) {
      DispatcherManager.deleteActionGroupOperations(deleteArray, index, deleteActionGroup);
    } else {
      let deleteItem = deleteArray[index];
      if (deleteItem) {
        remove(deleteArray, (item: any) => _.isEqual(item, deleteItem));
      }
    }
  }

  /**
   * If exists action name, then delete operations which contains that action name
   * @param {Operation[]} deleteArray Operation list
   * @param {number} index Deleted operation index
   * @param {string/boolean} deleteActionGroup Action name of operations that need to be deleted
   */
  private static deleteActionGroupOperations(deleteArray: Operation[], index: number, deleteActionGroup: string | boolean): void {
    let deleteItem;
    let length = deleteArray.length;

    while (length > index) {
      length--;
      deleteItem = deleteArray[length];
      if (deleteItem && !deleteItem.independent && deleteItem.actionName === deleteActionGroup) {
        remove(deleteArray, deleteItem);
      }
    }
  }

  /**
   * Entry point for dispatching actions from components that are not dispatcher modules.
   * @param {string} actionName Name of action that is dispatching.
   * @param {ActionType/Position/String} actionType Action configuration.
   * @param {any} data Data that will be added to dispatchers `appOperationData` before action is executed.
   */
  public dispatch(actionName: string, actionType?: ActionType | Position | string, data?: any): void {
    this.dispatchFromModule(null, actionName, actionType, data);
  }

  /**
   *
   * @param {string} moduleName If string then represents name of module component. If boolean than
   * @param actionName
   * @param actionType
   * @param data
   * @param methodName
   */
  public dispatchFromModule(moduleName: string | null, actionName: string, actionType?: ActionType | Position | string, data?: any, methodName?: string, specialData?: any): void {
    // <debug>
    if (data instanceof Event || _.get(data, 'nativeEvent') instanceof Event) {
      let acType = actionType || 'Position.end';
      let message: string = [`Error happened while passing data to "${actionName}" action. Native event passed as "data".`, `this.dispatch.bind(this, "${actionName}", ${acType});`, 'Example:', `<button onClick={this.dispatch.bind(this, "${actionName}", ${acType})} ></button>`, 'may result in passing native event to dispatcher data. To prevent this behaviour dispatch action like this:', `<button onClick={this.dispatch.bind(this, "${actionName}", ${acType}, {})} ></button>`, 'or', `<button onClick={() => this.dispatch("${actionName}", ${acType})} ></button>`].join('\n');
      throw new Error(message);
    }
    // </debug>

    if (moduleName && methodName) {
      const module = _.get(this.appModules, `${moduleName}[0]`);
      // <debug>
      if (!module) {
        throw new Error(`Module with name "${moduleName}" doesn't exists!`);
      }
      // </debug>
      const func = _.get(module, methodName);
      // <debug>
      if (!_.isFunction(func)) {
        throw new Error(`Method "${methodName}" doesn't exists in "${moduleName}" module.`);
      }
      // </debug>
      data = func.bind(module, data)();
      // <debug>
      if (_.isUndefined(data)) {
        throw new Error(`Method "${methodName}" returned ${data}. Passed parser function "${methodName}" has to return some value!`);
      }
      // </debug>
    }

    let dev;
    // <debug>
    dev = {
      inserted: !!this.appCurrentOperation, // if current operation exists, then next action is inserted
      actionThatTriggeredInserting: this.appCurrentOperation ? this.appCurrentOperation.actionName : actionName
    };
    // </debug>
    this.setSpecialOperationData(specialData);
    this.executeAction(actionName, actionType, data, dev);
  }

  private static functionExecution(argName: string, fnBody: string, invokeMethod: string, module: any, arg: any): any {
    // eslint-disable-next-line no-new-func
    return (Function(argName, fnBody) as any)[invokeMethod](module, arg);
  }

  // <debug>
  /**
   * Propagates additional properties needed for dispatcher editor debug mode.
   * @param operation
   * @param properties
   */
  private propagateAdditionalEditorOperationProperties = (operation: Operation, properties: {
    internalOperation?: boolean,
    complexParentOperationId?: string
  } = {
    internalOperation: false
  }) => {
    if (properties.internalOperation) {
      operation.internalOperation = properties.internalOperation;
    }
    if (_.isString(properties.complexParentOperationId)) {
      operation.complexParentOperationId = properties.complexParentOperationId;
    }
    if ([OperationType.conditional, OperationType.switch].includes(operation.type)) {
      Object.keys(operation).forEach((property: string) => {
        if (Array.isArray(operation[property])) {
          operation[property] = operation[property].map((op: any) => this.propagateAdditionalEditorOperationProperties(op, properties));
        }
      });
    }
    return operation;
  };

  // </debug>

  /**
   * Maps executed operations
   * @param {any} dev
   * @param {any} o
   */
  private mapExecutedOperation = (dev: any, o: any) => this.propagateAdditionalEditorOperationProperties(o, {
    internalOperation: dev.internalOperation,
    complexParentOperationId: dev.complexParentOperationId
  });

  /**
   * Executes global action
   * @param {string} actionName
   * @param {ActionType/Position/String} actionType If no undefined, operations which belong that action will be put to executed list
   * @param {any} data
   * @param {devType} dev
   */
  public executeAction(actionName: string, actionType: ActionType | string | Position = '', data?: any, dev?: any): void {
    actionType = DispatcherManager.getBufferActionType(actionType as ActionType, actionName);
    const actionTypeObj = DispatcherManager.getActionTypeObj(actionType);
    const dispatcherId = actionTypeObj.dispatcherId;
    const pos = actionTypeObj.pos;

    // <debug>
    if (dev && dev.inserted) {
      this.showInDispatcherEditor({
        actionName: dev.actionThatTriggeredInserting || '',
        name: actionName,
        actionType: actionTypeObj,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        independent: true,
        type: !!dev.stack ? 'executeStackAction' : 'executeAction'
      }, data || {});
      delete dev.inserted;
      delete dev.actionThatTriggeredInserting;
    }
    // </debug>

    if (!this.appDispatchers[MAIN_DISPATCHER_ID] && this.appDispatcherId === MAIN_DISPATCHER_ID) {
      this.appDispatchers[MAIN_DISPATCHER_ID] = this;
    }
    if (dispatcherId !== this.appDispatcherId) {
      // <debug>
      this.showInDispatcherEditor({
        actionName,
        dispatcherEditor: true,
        independent: true,
        dispatcherId: this.appDispatcherId,
        createdDispatcherId: dispatcherId,
        type: 'newDispatcherCreated'
      }, data || {});
      // </debug>
      this.executeActionUsingAlternativeDispatcher(actionName, actionTypeObj, data, dev);
    } else {
      if (actionTypeObj.stack) {
        this.setStack(actionName, actionTypeObj, data, dev);
        if (!this.appCurrentOperation) {
          this.startStackAction();
        }
      } else {
        if (this.appCurrentOperation) {
          if (actionType && pos !== Position.conditionallyExecuted) {
            let addExecutedOperation = this.getOperationByAction(actionName, actionTypeObj, data);
            const index = pos === Position.start ? 1 : this.appExecutedOperations.length;
            // <debug>
            const { debug = false, log = false } = dev || {};
            if (debug) {
              _.assign(addExecutedOperation[0], { debug });
            }
            if (log) {
              _.assign(addExecutedOperation[0], { log });
            }
            if (dev && (dev.internalOperation || _.isString(dev.complexParentOperationId))) {
              addExecutedOperation = addExecutedOperation.map(this.mapExecutedOperation.bind(this, dev));
              delete dev.internalOperation;
              delete dev.complexParentOperationId;
            }
            // </debug>
            spliceArray(this.appExecutedOperations, addExecutedOperation, index, 0);
            // <debug>
            this.logExecution(addExecutedOperation[0], true);
            // </debug>
          }
          // <debug>
          else {
            this.showInDispatcherEditor({
              actionName,
              actionType: actionTypeObj,
              dispatcherEditor: true,
              independent: true,
              dispatcherId: this.appDispatcherId,
              type: 'actionIsNotExecuted'
            }, data || {});
          }
          // </debug>
        } else {
          this.appExecutedOperations = this.getOperationByAction(actionName, actionTypeObj, data);
          // <debug>
          if (dev && (dev.internalOperation || _.isString(dev.complexParentOperationId))) {
            this.appExecutedOperations = this.appExecutedOperations.map(this.mapExecutedOperation.bind(this, dev));
            delete dev.internalOperation;
            delete dev.complexParentOperationId;
          }
          const { debug = false, log = false } = dev || {};
          if (debug) {
            _.assign(this.appExecutedOperations[0], { debug });
          }
          if (log) {
            _.assign(this.appExecutedOperations[0], { log });
          }
          // </debug>
          this.executeCurrentOperation();
        }
      }
    }
  }

  /**
   * Executes global action using alternative dispatcher instance
   * @param {string} actionName
   * @param {ActionType} actionTypeObj
   * @param {any} data
   * @param {devType} dev
   */
  private executeActionUsingAlternativeDispatcher(actionName: string, actionTypeObj: ActionType, data: any, dev?: devType): void {
    const { dispatcherId } = actionTypeObj;
    if (dispatcherId) {
      let dispatcher = this.appDispatchers[dispatcherId];
      if (!dispatcher) {
        dispatcher = new DispatcherManager({
          dialogChildren: this.getDialogChildrenRenderers(),
          appAllPersistentData: this.appAllPersistentData,
          appActions: this.appActions,
          appDeferredCounter: {},
          appDispatcherId: dispatcherId,
          appDispatchers: this.appDispatchers,
          appModules: this.appModules,
          appOperationData: _.cloneDeep(this.appOperationData),
          appStack: [],
          webSocket: this.webSocket,
          topLevelComponentName: this.topLevelComponentName,
          updateStateModules: this.updateStateModules,
          // <debug>
          DEVELOPMENT: _.cloneDeep(this.DEVELOPMENT)
          // </debug>
        });
        /* _.assign(dispatcher, {
          dialogChildren: this.getDialogChildrenRenderers(),
          appAllPersistentData: this.appAllPersistentData,
          appActions: this.appActions,
          appDeferredCounter: {},
          appDispatcherId: dispatcherId,
          appDispatchers: this.appDispatchers,
          appModules: this.appModules,
          appOperationData: _.cloneDeep(this.appOperationData),
          appStack: [],
          updateStateModules: this.updateStateModules
        });*/
        this.appDispatchers[dispatcherId] = dispatcher;

        // <debug>
        this.appDispatchers[dispatcherId].DEVELOPMENT.ENABLE_OPERATION_TIME_LOGGING = this.DEVELOPMENT.ENABLE_OPERATION_TIME_LOGGING;
        this.appDispatchers[dispatcherId].DEVELOPMENT.ENABLE_OPERATION_DEBUG = this.DEVELOPMENT.ENABLE_OPERATION_DEBUG;

        spliceArray(dispatcher.appExecutedOperations, [{
          actionName,
          name: actionName,
          actionType: actionTypeObj,
          dispatcherEditor: true,
          dispatcherId,
          independent: true,
          type: !actionTypeObj.stack ? 'executeAction' : 'executeStackAction'
        } as any], 0, 0);
        // </debug>
      } else {
        _.assign(dispatcher, {
          appDeferredCounter: {},
          appStack: []
        });
      }

      if (dispatcher.appCurrentOperation && dispatcher.appCurrentOperation.type === OperationType.request) {
        let operation = dispatcher.appCurrentOperation as RequestOperation;
        if (operation.mergeRequest) {
          let requestData = this.Axios.cancelMergeRequest(operation.mergeRequest);
          dispatcher.appExecutedOperations = [];
          // TODO log to DE that request is canceled and operations to execute are deleted
        }
      } else if (DispatcherManager.asyncRequestInProgress.length && this.appCurrentOperation && this.appCurrentOperation.type === 'action') {
        let idx: number = _.findIndex(DispatcherManager.asyncRequestInProgress, {
          actionName: this.appCurrentOperation.name
        });
        if (idx !== -1) {
          // @ts-ignore
          this.Axios.cancelMergeRequest(DispatcherManager.asyncRequestInProgress[idx].mergeRequest);
          dispatcher.appDependenceActions = [];
        }
      }

      dispatcher.executeAction(actionName, actionTypeObj, data, {
        // <debug>
        ...dev,
        inserted: true
        // </debug>
      });
    }
  }

  /**
   * Adds debug statement to function in development mode.
   * @param {Function} fn Function in which will be inserted debug statement in development mode.
   * @param {string} operationFn Function value from appModuleConfig operations fn property.
   * @param {Operation} operation Dispatcher's current operation
   * @return {any}
   */
  // <debug>
  private static functionWithDebugger(fn: Function, operationFn: string, operation: Operation = {
    name: operationFn
  }): any {
    let fnParts = fn.toString().split('\n');
    let firstLine = fnParts[0];
    let argName = 'arg';
    const fatArrow = ' => ';
    const isArrowFn = firstLine.includes(fatArrow) && !fn.name;
    const pattern = /\(.*\)/;

    if (_.get(fnParts, 'length') === 1 && !firstLine.includes(`${fatArrow}{`)) {
      fnParts = firstLine.split(fatArrow);
      firstLine = fnParts[0];
      fnParts[1] = `return ${fnParts[1]}`;
    } else {
      firstLine = firstLine.replace(/\s|=>.*/g, '');
      fnParts[fnParts.length - 1] = '';
    }

    if (isArrowFn && fn.length === 1) {
      firstLine = `(${firstLine})`; // arrow function with one argument, webpack stripes out parentheses
    }

    let newArgName = pattern.exec(firstLine);

    if (_.get(newArgName, 0) && newArgName && newArgName[0].match(/^\([\w|,\s]+\)$/)) {
      // noinspection RegExpSingleCharAlternation
      argName = newArgName[0].replace(/\(|\)/g, '');
    }
    if (Array.isArray(newArgName) && newArgName.length === 2) {
      argName = newArgName[1];
    }

    fnParts[0] = '\n// Executed method: ' + operationFn + '\n';

    // TODO create type
    return {
      argName,
      fnBody: fnParts.join('\n')
    };
  }

  // </debug>
  /**
   * Gets children renderers and props for warnings, dialogs and widgets.
   */
  public getDialogChildrenRenderers = (): ChildrenType[] => {
    let children: ChildrenType[] = this.dialogChildren;
    this.dialogChildren = []; // clean up
    return children;
  };

  /**
   * Handles `template` and `update` properties of given data object and add it on `appOperationData`.
   * e.g. When type of operation is `action` or `addOperationData`,
   * passed data object which contains `update` or `template` object will be added to `appOperationData` respectively.
   * If passed data object contains template object, all data from template object will be added to `appOperationData` or it will override currently added data
   * to `appOperationData` in case that they have the same name.
   * If passed data object contains update object, all data from update object will be updated with passed data and it will keep all other data from `appOperationData`.
   *
   * SomeAction: [...., {
            data: {
                update: {
                    folder: {
                        bind: {
                            tempJsonSearch: '{tempJsonSearch}' // Here we are adding `tempJsonSearch` property to `folder` object which is located at `appOperationData` if `folder`
                                                               // object doesn't have `tempJsonSearch`. In case that `folder` object already posses property `tempJsonSearch` it's value
                                                               // it will be replaced with new value.
                        }
                    }
                }
            },
            name: 'reloadFolderData',
            type: 'action'
        }]
   * @param {any} dataToTemplate Data to add to `appOperationData`.
   * @return {any}
   */
  public handleTemplatingAndUpdatingData(dataToTemplate: any): any {
    let data = _.cloneDeep(dataToTemplate);
    if (data) {
      let bindData;
      let bindType;
      let dataHandlingOrder = [];

      for (bindType in data) {
        if (data.hasOwnProperty(bindType) && contains(['template', 'update'], bindType)) {
          dataHandlingOrder.push(bindType);
        }
      }

      if (!dataHandlingOrder.length) {
        _.assign(this.appOperationData, data);
      }

      for (let i = 0; i < dataHandlingOrder.length; i++) {
        bindType = dataHandlingOrder[i];
        bindData = _.get(data, bindType);
        if (bindData) {
          _.unset(data, bindType);
          bindData = this.getBindDataObj(bindData, bindType === 'update');

          // <debug>
          const interception = _.intersection(Object.keys(bindData), Object.keys(data));
          const interceptionLength = interception.length;
          if (interceptionLength && dataHandlingOrder[0] === 'update') {
            throw new Error(`${interception.join(', ')} ${interceptionLength === 1 ? 'is' : 'are'} ${dataHandlingOrder[0]}d and after that ${dataHandlingOrder[1]}d. Is this intentional?`);
          }
          // </debug>

          if (bindType === 'update') {
            getMergeUpdateObj(dataToTemplate, bindData, this.appOperationData);
          } else {
            _.assign(this.appOperationData, bindData);
          }

          _.assign(data, bindData);
        }
      }
    }
    return data;
  }

  /**
   * Evaluates dispatcher operation properties which are stated in `OPERATION_PROPERTIES_TO_EVALUATE` property.
   * @param {Operation} operation Current operation data.
   * @param {string[]} propertiesToEvaluate
   * @return {Operation}
   */
  private evaluateOperationValue(operation: Operation, propertiesToEvaluate: string[] = DispatcherManager.OPERATION_PROPERTIES_TO_EVALUATE): Operation {
    let prop;
    let item = 0;
    let value;
    while (item < propertiesToEvaluate.length) {
      prop = propertiesToEvaluate[item];
      value = _.get(operation, prop);
      if (value && (value.match(/\?/gi) || value.indexOf('{') !== -1)) {
        // <debug>
        // evaluated keeps key - value pairs for Dispatcher Editor debug mode
        if (!_.isObject(operation.evaluated)) {
          operation.evaluated = {};
        }
        operation.evaluated[prop] = value;
        // </debug>
        let evaluatedValue: any = this.getTemplateExpression(value, true);
        _.set(operation, prop, evaluatedValue);
      }
      item++;
    }
    return operation;
  }

  // <debug>
  private logExecution(operation: Operation, end: boolean) {
    let { log = false } = operation;
    if (log) {
      if (_.isString(log) && !_.get(this.DEVELOPMENT.LOG_ACTION_NAMES, log)) {
        _.set(this.DEVELOPMENT.LOG_ACTION_NAMES, log, '#' + (((1 << 24) * Math.random()) | 0).toString(16));
      }
      if (this.DEVELOPMENT.OPERATION_LOG_DATA) {
        console.log(_.clone(this.appOperationData));
      }
    }
    if (_.includes(Object.keys(this.DEVELOPMENT.LOG_ACTION_NAMES), operation.actionName)) {
      log = this.DEVELOPMENT.LOG_ACTION_NAMES[operation.actionName as string];
    }

    if (operation.type === OperationType.generateOperation) {
      this.timeLogDynamic(operation, log);
    } else {
      this.timeLog(operation.actionName as string, operation.moduleName as string, operation.name as string, end, log);
    }
  }

  // </debug>

  /**
   * Execute individual operation if type is not action/switch/conditional
   * @param {Operation} currentOperation Current operation data
   * @param {Boolean} operationEnd  If true previous operation is executed
   * @param {Boolean} manipulateWithPersistent If `true` data will be added or removed as persistent based on currentOperation type
   */
  private executeOperation(currentOperation: Operation, operationEnd: boolean, manipulateWithPersistent?: boolean): void {
    if (currentOperation.evaluate) {
      currentOperation = this.evaluateOperationValue(currentOperation, currentOperation.propertiesToEvaluate);
      _.unset(currentOperation, 'evaluate');
    }
    const { synchronous, moduleName, continueAnyway = false } = currentOperation;
    const aggregatedResult = _.get(currentOperation, 'aggregatedResult');
    const isPromiseAggregateExecute = _.get(currentOperation, 'isPromiseAggregateExecute');
    let deleteActionGroup: boolean | string | undefined = !continueAnyway;
    let deleteAllOperations = false;
    let moduleGroup: any[] = this.getModuleGroup(moduleName as string, currentOperation);
    let { isPromise, stopExecOperations, delay } = currentOperation as Operation;
    let shallPromiseExecute = false;
    let isPromiseAggregate = false;
    // <debug>
    const executedSynchronous = synchronous || (isPromise && _.isUndefined(synchronous));
    if (aggregatedResult && !executedSynchronous && (delay > 0 || isPromise)) {
      throw new OperationError('You can use "aggregateResult" property as true if operation type is addOperationData and operation must be synchronous.', currentOperation);
    }
    // </debug>
    if (Array.isArray(moduleGroup)) {
      let arg;
      const operationActionName = currentOperation.actionName as string;
      // <debug>
      if (currentOperation.type !== OperationType.addOperationData) {
        this.logExecution(currentOperation, operationEnd);
      }
      // </debug>
      let module;
      let result;
      if (!isPromiseAggregateExecute) {
        this.aggregatedResults = aggregatedResult ? [] : null;
      }
      const data = this.handleTemplatingAndUpdatingData(_.cloneDeep(currentOperation.data));
      let operationItem;
      let additionalResultObj;
      let moduleConfig;
      let lastExecutedModule = false;

      let error: string | null = moduleGroup.length ? null : `Module with name "${moduleName}" doesn't exists or it's not mounted yet!`;
      isPromiseAggregate = isPromise && aggregatedResult && moduleGroup.length > 1;
      shallPromiseExecute = isPromise && !isPromiseAggregate && !!moduleGroup.length;
      if (isPromiseAggregate) {
        const promiseAggregateOperations = [];
        moduleGroup.forEach((moduleItem, moduleIndex) => {
          promiseAggregateOperations.push({
            ...currentOperation,
            executeType: 'index',
            index: moduleIndex,
            isPromiseAggregateExecute: true,
            internalOperation: true
          });
        });
        spliceArray(this.appExecutedOperations, promiseAggregateOperations, 1, 0);
        deleteActionGroup = undefined;
      } else {
        for (let i = 0; i < moduleGroup.length; i++) {
          if (i === moduleGroup.length - 1) {
            lastExecutedModule = true;
          }
          module = moduleGroup[i];
          operationItem = this.getOperationItem(module, currentOperation);
          arg = this.getOperationArg(operationItem.args, data, operationItem.enableArrayArgs);
          if (isPromise) {
            this.stopExecOperations = !!stopExecOperations;
            // noinspection JSIgnoredPromiseFromCall
            this.doPromise({
              arg,
              operation: currentOperation as PromiseOperation,
              module,
              operationItem
            }).catch(this.handlePromiseCatch);
          } else {
            moduleConfig = module.appModuleConfig;
            additionalResultObj = {
              currentOperation,
              moduleConfig
            };
            if (!_.isFunction(module[operationItem.fn]) && module.constructor && _.isFunction(module.constructor[operationItem.fn])) {
              module = module.constructor;
            }
            result = this.getResult(module, operationItem.fn, arg, additionalResultObj, true, lastExecutedModule);
            // <debug>
            if (_.get(result, 'dispatcherEditor')) {
              error = result.error;
              result = undefined;
            }
            // </debug>
            if (!additionalResultObj.currentOperation.delay) {
              moduleConfig = this.getModuleConfig(result, moduleConfig, currentOperation);
            }
            // <debug>
            if (currentOperation.type === OperationType.addOperationData) {
              this.logExecution(currentOperation, operationEnd);
            }
            // </debug>

            // Old code (2 rows below). We don't use now that implementation
            deleteActionGroup = result === 'deleteActionGroup' ? operationActionName : undefined;
            deleteAllOperations = result === 'deleteAllOperations';
          }
          if (deleteActionGroup || deleteAllOperations) {
            break;
          }
        }
      }
      if (deleteActionGroup === true || error !== null) {
        // <debug>
        if (error) {
          this.appExecutedOperations.splice(1, 0, {
            dispatcherEditor: true,
            dispatcherId: this.appDispatcherId,
            independent: true,
            error,
            type: 'previousOperationFailedToExecute'
          } as any);
        }
        // </debug>
        if (deleteActionGroup) {
          deleteActionGroup = operationActionName;
        }
      }
      currentOperation.independent = false;
      // if (currentOperation.independent) {
      //   delete currentOperation.independent;
      // }
    }

    if (manipulateWithPersistent) {
      this.handleTemplatingAndUpdatingData(_.cloneDeep(currentOperation.data));
      const persistent = currentOperation.persistent || [];
      this.manipulateWithPersistentData(currentOperation.type as OperationType, persistent as string[]);
      deleteActionGroup = false;
    }

    const actionName = currentOperation.actionName || '';
    if (actionName[0] === '_' && currentOperation.name === 'hide' && _.includes(['WarningDialog', 'Dialog'], moduleName)) {
      spliceArray(
        this.appExecutedOperations,
        [{
          name: actionName,
          moduleName,
          type: OperationType.cleanUpAction
        }],
        1,
        0
      );
    }

    if (!(synchronous || shallPromiseExecute)) {
      this.backToDispatcher(false, deleteActionGroup, deleteAllOperations);
    }
  }

  /**
   * If type in operation is action, then execute that action from start.
   * @param {ActionOperation} currentOperation Current operation data
   */
  private executeActionFromOperation(currentOperation: ActionOperation) {
    let { evaluate = false, name, data } = currentOperation;
    this.handleTemplatingAndUpdatingData(data);
    if (name) {
      if (evaluate) {
        name = this.getTemplateExpression(name, true);
        // <debug>
        // @ts-ignore
        currentOperation = this.evaluateOperationValue(currentOperation);
        // </debug>
      }
      // <debug>
      if (name.match(/\?/)) {
        throw new OperationError(`Action with name "${name}" doesn't exists. Set evaluate property to "true".`, currentOperation);
      }
      // </debug>
      currentOperation.name = name;
    }

    // this.handleTemplatingAndUpdatingData(currentOperation.data);
    let actionType: ActionType = currentOperation.actionType as ActionType;
    if (_.isEmpty(actionType)) {
      actionType = {};
    }

    if (actionType.template) {
      actionType = {
        ...actionType,
        ...this.getBindDataObj(actionType.template)
      };
      delete actionType.template;
    }

    let { propagationIndependent = null } = actionType;
    _.assign(actionType, {
      pos: Position.start,
      propagationIndependent
    });
    this.executeAction(currentOperation.name as string, actionType, undefined, {
      // <debug>
      internalOperation: currentOperation.internalOperation,
      complexParentOperationId: currentOperation.complexParentOperationId,
      debug: currentOperation.debug,
      log: currentOperation.log
      // </debug>
    });

    const { subName } = this.appExecutedOperations[0];

    removeAt(this.appExecutedOperations, 0);
    if (this.appExecutedOperations.length) {
      // <debug>
      const { debug } = this.appExecutedOperations[0];
      if (debug) {
        // passing debug from action to first operation in action
        this.appExecutedOperations[0].debug = debug;
      }
      // </debug>
      if (subName) {
        // passing debug from action to first operation in action
        // TODO implement
        this.appExecutedOperations[0].subName = subName;
      }
    }
    this.executeCurrentOperation();
  }

  private cleanUpActions(operation: Operation) {
    const { dispatcherId = this.appDispatcherId } = operation;
    const dispatcher = this.appDispatchers[dispatcherId] || this.appDispatchers[MAIN_DISPATCHER_ID];

    if (operation.evaluate) {
      operation = dispatcher.evaluateOperationValue(operation);
    }
    // <debug>
    this.logExecution(operation, true);
    // </debug>

    if ((operation.name as string)[0] === '_') {
      const pattern = new RegExp(`^_${operation.moduleName}`, 'g');
      const toRemove = Object.keys(this.appActions).filter(key => !!key.match(pattern));
      for (let i = 0; i < toRemove.length; i++) {
        _.unset(dispatcher.appActions, toRemove[i]);
      }
    } else {
      _.unset(dispatcher.appActions, operation.name as string);
    }
    this.backToDispatcher();
  }
  public moduleMounted(operation: IsModuleMountedOperation) {
    const { mappingName = 'isModuleMounted' } = operation;
    const { moduleName, moduleId } = this.appOperationData;
    if (_.get(this.appModules, moduleName)) {
      const modules = this.appModules[moduleName];
      this.appOperationData[mappingName] = moduleId ? modules.some(m => m['moduleId'] === moduleId) : modules.length > 0;
    }
    this.backToDispatcher();
  }

  /**
   * Prepares and executes current operation
   * @param {Boolean} operationEnd If true previous operation is executed
   */
  private executeCurrentOperation(operationEnd: boolean = false): void {
    if (!this.stopExecOperations) {
      this.appCurrentOperation = this.appExecutedOperations[0];
      let currentOperation = this.appCurrentOperation as Operation;
      // <debug>
      // @ts-ignore
      while (currentOperation && currentOperation.dispatcherEditor) {
        if ((currentOperation as any).condition) {
          this.conditionOrder[this.appDispatcherId].pop();
        }
        // @ts-ignore
        let { editorData } = this.appCurrentOperation;
        // @ts-ignore
        delete this.appCurrentOperation.editorData;
        this.showInDispatcherEditor(this.appCurrentOperation, editorData);
        this.appExecutedOperations.shift();
        this.appCurrentOperation = this.appExecutedOperations[0];

        currentOperation = this.appCurrentOperation as Operation;
      }
      // </debug>
      if (currentOperation) {
        // eslint-disable-next-line sonarjs/max-switch-cases
        switch (currentOperation.type) {
          case OperationType.cancelRequest:
            const operationData: any = _.cloneDeep(currentOperation.data);
            const {
              cancelRequestForFile,
              cancelRequestForUploadGroup
            } = this.handleTemplatingAndUpdatingData(operationData);
            this.cancelRequest(cancelRequestForFile, cancelRequestForUploadGroup);
            break;
          case OperationType.cleanUpAction:
            this.cleanUpActions(currentOperation);
            break;
          case OperationType.action:
            this.executeActionFromOperation(currentOperation as ActionOperation);
            break;
          case OperationType.addPersistentData:
          case OperationType.removePersistentData:
            // <debug>
            if (currentOperation.debug) {
              error(`debug property won't take effect for operation types "${OperationType.addPersistentData}" and "${OperationType.removePersistentData}". Warning at action "${currentOperation.actionName}".`, 'info');
            }
            this.logExecution(currentOperation, operationEnd);
            // </debug>
            this.executeOperation(currentOperation, operationEnd, true);
            break;
          case OperationType.closeWindow:
            this.handleCloseOperation(currentOperation as CloseOperation);
            break;
          case OperationType.conditional:
            this.switchOperations(currentOperation as ConditionOperation, true);
            break;
          case OperationType.cookie:
            this.handleCookieOperation(currentOperation as CookieOperation);
            break;
          case OperationType.data:
            this.setOperationData(currentOperation, false);
            break;
          case OperationType.dialog:
            this.dialogOperation(OperationType.dialog, currentOperation as DialogOperation);
            break;
          case OperationType.updateDialog:
            this.updateDialogOperation(currentOperation as UpdateDialog);
            break;
          case OperationType.maskModule:
            this.maskOperations(currentOperation as MaskOperation);
            break;
          case OperationType.mergeAsyncRequest:
            this.handleMergeAsyncRequestOperation(currentOperation as MergeAsyncRequest);
            break;
          case OperationType.unmaskModule:
            this.maskOperations(currentOperation as UnmaskOperation);
            break;
          case OperationType.unmaskAllModule:
            this.unmaskAllModuleOperations(currentOperation);
            break;
          case OperationType.signIn:
          case OperationType.signOut:
            this.handleSignOperation(currentOperation as SignOperation);
            break;
          case OperationType.stack:
            this.stackOperation(currentOperation as StackOperation);
            break;
          case OperationType.stopAll:
            // <debug>
            this.showInDispatcherEditor(currentOperation);
            // </debug>
            this.stopAll(currentOperation as StopAllOperation);
            break;
          case OperationType.switch:
            this.switchOperations(currentOperation as SwitchOperation);
            break;
          case OperationType.syncDispatchersDataItem:
            // <debug>
            this.logExecution(currentOperation, operationEnd);
            // </debug>
            this.syncDispatchersDataItem(currentOperation as SyncDispatcherItemsOperation);
            break;
          case OperationType.language:
            this.handleLanguageOperation(currentOperation as LanguageOperation);
            break;
          case OperationType.theme:
            this.handleThemeOperation(currentOperation as ThemeOperation);
            break;
          case OperationType.pingDownload:
            // <debug>
            this.logExecution(currentOperation, operationEnd);
            // </debug>
            this.pingDownloadOperation(currentOperation as PingDownloadOperation);
            break;
          case OperationType.popupWindow:
            this.popupWindowOperation(currentOperation as PopupWindowOperation);
            break;
          case OperationType.redirect:
            this.handleRedirectOperation(currentOperation as RedirectOperation);
            break;
          case OperationType.unlink:
            // <debug>
            this.showInDispatcherEditor(currentOperation);
            // </debug>
            this.unlinkDispatcher(currentOperation as UnlinkOperation);
            break;
          case OperationType.upload:
            this.uploadOperation(currentOperation as UploadOperation);
            break;
          case OperationType.isModuleMounted:
            this.moduleMounted(currentOperation as IsModuleMountedOperation);
            break;
          case OperationType.request:
            const { stopExecOperations } = currentOperation;
            this.stopExecOperations = !!stopExecOperations;
            if (currentOperation.debug) {
              // <debug>
              let parts = this.request.toString().split('\n');
              let argName: string = parts[0];
              parts.pop();
              parts.shift();
              argName = argName.split('(')[1];
              argName = argName.split(')')[0];
              let fnBody: string = `(async (${argName}) => {\n${DispatcherManager.DEBUG_EXPRESSION}\n ${parts.join('\n')} })(${argName})`;
              fnBody = fnBody.replace(/DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME/g, `"${DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME}"`);
              DispatcherManager.functionExecution(argName, fnBody, 'call', this, currentOperation);
              // </debug>
            } else {
              this.request(currentOperation as RequestOperation).catch(this.handlePromiseCatch);
            }
            break;
          case OperationType.warning:
            this.dialogOperation(OperationType.warning, currentOperation as WarningOperation);
            break;
          case OperationType.widget:
            this.widgetOperation(currentOperation as WidgetOperation);
            break;
          case OperationType.windowManager:
            this.windowManagerOperation(currentOperation as WindowManagerOperation);
            break;
          case OperationType.webSocket:
            this.webSocketOperation(currentOperation as WebSocketOperation);
            break;
          case OperationType.webSocketFakePing:
            this.webSocketFakePingOperation(currentOperation as WebSocketFakePingOperation);
            break;
          default:
            let executed = true;
            if (currentOperation.waitForModule && currentOperation.moduleName) {
              if (currentOperation.evaluate) {
                currentOperation = this.evaluateOperationValue(currentOperation);
                _.unset(currentOperation, 'evaluate');
              }
              let mountedModules = [];
              const executeTypeIsVisible = _.get(currentOperation, 'executeType') === 'visible';
              let mountedVisibilityModules = [];
              if (_.get(this.appModules, `${currentOperation.moduleName}.length`)) {
                mountedModules = _.get(this.appModules, currentOperation.moduleName).filter((module: any) => module.isModuleMounted);
                if (mountedModules.length && executeTypeIsVisible) {
                  mountedVisibilityModules = mountedModules.filter((module: any) => module.isVisible());
                }
              }
              if (!(mountedModules.length && (!executeTypeIsVisible || mountedVisibilityModules.length))) {
                executed = false;
                this.appWaitingForModule = currentOperation.moduleName;
              }
            }
            if (executed) {
              this.appWaitingForModule = '';
              delete currentOperation.waitForModule;
              this.executeOperation(currentOperation, operationEnd);
            }
            break;
        }
      } else {
        // <debug>
        this.timeLog('', '', '', false);
        // </debug>
        if (this.startStackAction()) {
          this.clearAllOperationData();
        }
      }
    }
  }

  /**
   * Returns state of WindowManager class by methods ('getOpenedWindowsCount', 'isActiveWindowDialog', 'isActiveWindowWarning')
   * @param {WindowManagerOperation} operation
   */
  private windowManagerOperation = (operation: WindowManagerOperation) => {
    const { name, mappingName } = operation;
    const result = DispatcherWindowManager[name]();
    _.set(this.appOperationData, mappingName || name, result);
    this.backToDispatcher();
  }

  /**
   * Connects to socket server or sets session operations
   * @param {WebSocketOperation} operation
   */
  private webSocketOperation = (operation: WebSocketOperation) => {
    if (this.webSocket) {
      operation = this.evaluateOperationValue(operation, ['socketId']) as WebSocketOperation;
      let { socketId, connected, operations, requestConfig } = operation;
      if (connected) {
        this.webSocket.connect();
      } else {
        this.webSocket.setSessionOperations(operations, socketId, requestConfig);
      }
    } else {
      this.backToDispatcher();
      if (operation?.operations) {
        // <debug>
        throw new OperationError('Sockets don\'t work. Probably, You should set properties.config.(development/production).js file with WEB_SOCKET configuration!', operation);
        // </debug>
      }
    }
  }

  /**
   * It triggers socket operations by socketId
   * @param {WebSocketFakePingOperation} operation
   */
  private webSocketFakePingOperation = (operation: WebSocketFakePingOperation) => {
    if (this.webSocket) {
      operation = this.evaluateOperationValue(operation, ['socketId', 'data']) as WebSocketFakePingOperation;
      let { socketId, data, delay = 5000, operations } = operation;
      this.webSocket.setSessionOperations(operations, socketId);
      setTimeout(() => {
        this.webSocket.fakePingOperations({
          id: socketId,
          data
        });
      }, delay);
    } else {
      this.backToDispatcher();
    }
  }

  /**
   * Convert pingDownload operation into multiple operations in order to ping until download is ready to download file.
   * @param o
   */
  private pingDownloadOperation = (o: PingDownloadOperation) => {
    const { params } = this.appOperationData;
    let pingDownload: PingDownload = new PingDownload(o, params, this.appDispatcherId);
    let pingAction: Action = pingDownload.getOperations();

    if (this.appDispatcherId === pingDownload.callingDispatcherId) {
      this.executeAction(o.actionName as string, {
        stack: true,
        pos: Position.end,
        dispatcherId: pingDownload.asyncDispatcherId
      }, undefined, {
        // <debug>
        inserted: true,
        stack: true
        // </debug>
      });
    } else {
      // eslint-disable-next-line array-bracket-newline
      spliceArray(this.appExecutedOperations, [
        // <debug>
        ...pingDownload.getComplexOperationStart(o),
        // eslint-disable-next-line array-element-newline
        // </debug>
        ...pingAction], 1, 0);
    }
    pingDownload = null;
    this.backToDispatcher();
  };

  /**
   * Redirect method passed from abstract component.
   * @param path
   * @param forceRefresh
   * @param cookieConsent
   */
  public redirectTo(path: string, forceRefresh: boolean = false, cookieConsent: boolean = false) {
  }

  /**
   * Executes stack action
   * @param {StackOperation} action
   * @param {boolean} immediately
   */
  private executeStackAction(action: StackOperation, immediately: boolean = false): void {
    const startTime = action.startTime;
    action = findBy(this.appStack, DispatcherManager.hasStackItemWithActionName.bind(this, action.actionName as string));
    if (action) {
      const actionName: string = action.actionName as string;
      const isDeferredActionTime = this.appDeferredCounter[actionName];
      if (immediately || isDeferredActionTime === startTime) {
        remove(this.appStack, action);
        const actionType = _.merge(_.clone(DispatcherManager.DEFAULT_ACTION_TYPE), action.actionType, {
          pos: Position.end,
          stack: false // has to be false, if true, action will be pushed to stack again instead of executing
        });
        this.executeAction(actionName, actionType, action.data, {
          // <debug>
          debug: action.debug,
          log: action.log,
          inserted: true,
          actionThatTriggeredInserting: actionName,
          stack: true
          // </debug>
        });
      }
    }
  }

  /**
   * Returns action type properties
   * @param {String/ActionType} type
   * @returns {ActionType}
   */
  private static getActionTypeObj(type: ActionType | Position) {
    let typeObj = _.clone(DispatcherManager.DEFAULT_ACTION_TYPE);
    if (_.isString(type)) {
      _.set(typeObj, 'pos', type);
    } else if (_.isObject(type)) {
      _.merge(typeObj, type);
    }
    return typeObj;
  }

  /**
   * Returns data object based on template and update data object
   * @param {any} data
   * @param {boolean} isUpdate Is `true` if we update dispatcher `appOperationData`
   * @return {any}
   */
  private getBindDataObj(data: any, isUpdate: boolean = false): any {
    let dataItem;

    for (const prop in data) {
      if (data.hasOwnProperty(prop) && prop !== 'loop') {
        dataItem = data[prop];
        if (prop === 'bind') {
          data = this.handleBinding(data, dataItem, isUpdate);
        } else if (_.isObject(dataItem) || Array.isArray(dataItem)) {
          data[prop] = this.getBindDataObj(dataItem, isUpdate);
        }
      }
    }
    return data;
  }

  /**
   * Returns converted actionType object. Buffer property changes to appropriate properties.
   * @param {ActionType} actionType
   * @param {string} actionName
   * @returns {ActionType}
   */
  private static getBufferActionType(actionType: ActionType, actionName: string): ActionType {
    if (_.isObject(actionType) && !_.isUndefined(actionType.buffer)) {
      if (Array.isArray(actionType.canceledStackActions)) {
        actionType.canceledStackActions.push(actionName);
      } else {
        actionType.canceledStackActions = [actionName];
      }
      actionType.stack = true;
      if (!actionType.pos) {
        actionType.pos = Position.start;
      }
      actionType.delay = actionType.buffer;
      _.unset(actionType, 'buffer');
    }
    return actionType;
  }

  /**
   * Adds member to array when operation is independent
   * @param {Operation} operation
   * @returns {boolean}
   */
  private static getIndependentOperation(operation: Operation): boolean {
    return !!operation.independent;
  }

  /**
   * Returns arg as object with properties which matching with properties from action data or dispatcher data, for dispatcher methods
   * @param {string[]} args List of properties for arg object of dispatcher method
   * @param {any} data Action params
   * @param {boolean} enableArrayArgs If true, operation method will get max 3 arguments that specified in args property, otherwise operation method will get one argument as object with properties, that specified in args property
   * @returns {any}
   */
  private getOperationArg(args: string[], data: any, enableArrayArgs: boolean): any {
    let arg: any = {
      dispatcherId: this.appDispatcherId
    };
    const argsLength = args.length;
    if (argsLength) {
      enableArrayArgs = enableArrayArgs && argsLength < DispatcherManager.MAX_NUMBER_OF_FUNCTION_ARGS;
      if (enableArrayArgs) {
        arg = [];
      }
      let value;
      let propertyName;
      for (let i = 0; i < argsLength; i++) {
        propertyName = args[i];
        if (/^{{[a-zA-Z_]+[\w_]*}}$/.test(propertyName)) {
          value = this.appSpecialOperationData[propertyName.replace(/[{}]/g, '')];
        } else {
          value = this.appOperationData[propertyName];
          if (data && !_.isUndefined(data[propertyName])) {
            value = data[propertyName];
          }
        }
        if (enableArrayArgs) {
          arg[i] = value;
        } else {
          arg[propertyName] = value;
        }
      }
    }
    return arg;
  }

  /**
   * Ensures that dependence and subName of operation are unique.
   * @param operation
   * @param matchSubName
   * @param uniqueId
   */
  private static createUniqueDependenceName = (operation: Operation, matchSubName: string, uniqueId: string) => {
    const { dependence, subName, actionType } = operation;

    if (Array.isArray(_.get(actionType, 'loop.breakOperations'))) {
      actionType.loop.breakOperations = actionType.loop.breakOperations.map((operation: Operation) => DispatcherManager.createUniqueDependenceName(operation, subName as string, uniqueId));
    }

    if (dependence && Array.isArray(dependence) && dependence.includes(matchSubName)) {
      let index: number = dependence.indexOf(matchSubName);
      dependence[index] = uniqueId;
      operation.dependence = dependence;
    }

    if ([subName, dependence].indexOf(matchSubName) > -1) {
      if (dependence) {
        operation.dependence = uniqueId;
      } else if (subName) {
        operation.subName = uniqueId;
      }
    }
    return operation;
  };

  /**
   * Returns list of action operations.
   * @param {string} actionName
   * @param {ActionType/Position} actionType
   * @param {any} data Action params
   * @returns {any[]}
   */
  private getOperationByAction(actionName: string, actionType: ActionType | Position, data: any): any[] {
    if (_.get(this.appCurrentOperation, 'evaluate') && contains(DispatcherManager.OPERATION_PROPERTIES_TO_EVALUATE, 'name')) {
      actionName = !!actionName.match(/\?/) ? this.getTemplateExpression(actionName, true) : actionName;
      _.unset(this.appCurrentOperation, 'evaluate');
    }
    let action = this.appActions[actionName];
    // <debug>
    if (_.isUndefined(action)) {
      throw new Error(`Action name "${actionName}" is not registered on dispatcher or doesn't exist!`);
    }
    if (_.isFunction(action)) {
      throw new Error(`Action name "${actionName}" can't be function!`);
    }
    // </debug>

    let dataToPersist: string[] = _.get(actionType, 'persistent');
    if (Array.isArray(dataToPersist)) {
      this.manipulateWithPersistentData(OperationType.addPersistentData, dataToPersist);
    }

    let operations = this.createOperationProperties(_.cloneDeep(action) as Operation[], actionName, actionType);

    let subNameOperations: Operation[] = operations.filter((operation: Operation) => operation.subName);
    let subNameOperationsLength = subNameOperations.length;
    if (subNameOperationsLength) {
      for (let i = 0; i < subNameOperationsLength; i++) {
        const { subName } = subNameOperations[i];
        const uniqueId = `${subName}_${uuid()}`;
        operations = operations.map((operation: Operation) => DispatcherManager.createUniqueDependenceName(operation, subName as string, uniqueId));
        if (Array.isArray(this.appDependenceActions)) {
          this.appDependenceActions = this.appDependenceActions.map((operation: Operation) => DispatcherManager.createUniqueDependenceName(operation, subName as string, uniqueId));
        }
      }
    }

    if (data) {
      spliceArray(operations, [{
        actionName,
        actionType,
        data,
        internalOperation: true,
        type: OperationType.data
      }], 0, 0);
    }
    // <debug>
    else {
      spliceArray(operations, [{
        actionName,
        actionType,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        independent: true,
        type: 'actionCalledWithoutData'
      } as any], 0, 0);
    }
    // </debug>
    return operations;
  }

  /**
   * Returns operation item config. If type of operation is 'generateOperation' it creates new specific config.
   * @param {any} module Module component.
   * @param {Operation} currentOperation Current operation config from current action.
   * @return {any}
   */
  private getOperationItem(module: any, currentOperation: Operation): any {
    const { name, debug } = currentOperation;
    let appModuleConfig = module.appModuleConfig || {};
    let operationItem = _.get(appModuleConfig.operations, name as string);
    if (!operationItem) {
      _.set(appModuleConfig, `operations.${name}`, {
        args: currentOperation.args || [],
        enableArrayArgs: true,
        fn: name
      });
      operationItem = _.get(appModuleConfig.operations, name as string);
    } else {
      _.merge(operationItem, {
        args: currentOperation.args
      });
      _.assign(currentOperation, {
        args: operationItem.args
      });
    }
    module.appModuleConfig = appModuleConfig;
    if (currentOperation.type === OperationType.generateOperation) {
      let args = currentOperation.args as string[];
      const getValue = currentOperation.data.getValue;
      const mappingName = currentOperation.mappingName;
      const scope = currentOperation.data.scope;

      if (_.isArray(args)) {
        if (!contains(args, 'fn')) {
          args.push('fn');
        }
      } else {
        args = ['fn'];
      }

      operationItem = {};

      if (!_.isUndefined(getValue)) {
        // <debug>
        if (!_.isString(getValue)) {
          throw new OperationError(`"getValue" property has to be string when type of operation is ${OperationType.generateOperation}.`, currentOperation);
        }
        // </debug>
        operationItem.getValue = getValue;
        if (!contains(args, 'getValue')) {
          args.push('getValue');
        }
      }

      if (scope && !contains(args, 'scope')) {
        args.push('scope');
      }

      _.assign(operationItem, {
        args,
        debug,
        fn: DispatcherManager.EXECUTE_NATIVE_METHOD,
        scope
      });

      if (mappingName) {
        operationItem.mappingName = mappingName;
      }
      // <debug>
      else {
        throw new OperationError(`When type of operation is "${OperationType.generateOperation}" mappingName property must be provided!`, currentOperation);
      }
      // </debug>
      const persistent = currentOperation.persistent;
      if (persistent) {
        this.manipulateWithPersistentData(OperationType.addPersistentData, (Array.isArray(persistent) ? persistent : [mappingName]) as string[]);
      }
    }
    return operationItem;
  }

  /**
   * Get module group based on module name
   * @param {string} moduleName Current operation module name.
   * @param operation
   * @param isMaskType True if passed operation is type of MaskOperation or UnmaskOperation.
   * @returns {any} Module group with module name and operations
   */
  private getModuleGroup(moduleName: string, operation: Operation, isMaskType: boolean = false): any[] {
    if (operation.evaluate) {
      operation = this.evaluateOperationValue(operation);
      _.unset(operation, 'evaluate');
    }
    let moduleGroup: any[] = [];
    if (_.isString(moduleName)) {
      moduleGroup = moduleName ? this.appModules[moduleName] : [];
      if (moduleName && !moduleGroup) {
        const match = moduleName.match(/\?/);

        // <debug>
        if (match) {
          throw new Error(`Module with name "${moduleName}" doesn't exists. Set evaluate property to "true".`);
        }
        // </debug>

        if (!match) {
          // <debug>
          error(`Module with name "${moduleName}" doesn't exists or it's not mounted yet!`);
          // </debug>
          this.appModules[moduleName] = []; // faking module until it's not registered od dispatcher
          moduleGroup = [];
        }
      }
    }
    let { moduleId, executeType, index, displayed = false } = operation;
    let moduleShouldBeExecuted: boolean;
    moduleGroup = moduleGroup.filter((module: any, ind: number) => {
      moduleShouldBeExecuted = false;
      let isVisible: boolean = DispatcherManager.isModuleVisible(module);
      let isDisplayed: boolean = DispatcherManager.isModuleDisplayed(module);
      if (module && (executeType !== 'visible' || isVisible) && (!displayed || isDisplayed)) {
        moduleShouldBeExecuted = !(executeType === 'index' && index !== ind) && !(moduleId && module.moduleId !== moduleId);
        if (isMaskType && moduleShouldBeExecuted) {
          if (executeType === 'widget' && module.moduleId && this.appOperationData.moduleId !== module.moduleId) {
            moduleShouldBeExecuted = false;
            return moduleShouldBeExecuted;
          }
          moduleShouldBeExecuted = isVisible && !DispatcherManager.isModuleCollapsed(module);
        }
      }
      return moduleShouldBeExecuted;
    });
    return moduleGroup;
  }

  /**
   * Get module config according on operation type(addOperationData/deleteAllOperationData)
   * @param {any} result Data for operation data array
   * @param {any} moduleConfig Module config which contains module name and operations
   * @param {Operation} currentOperation Current operation from current action
   * @returns {any} Modify and return module config depends on operation type(addOperationData/deleteAllOperationData)
   */
  private getModuleConfig(result: any, moduleConfig: any, currentOperation: Operation): any {
    const operationType = currentOperation.type;

    if (operationType === OperationType.addOperationData || (operationType === OperationType.generateOperation && currentOperation.mappingName)) {
      if (operationType === OperationType.generateOperation) {
        _.assign(currentOperation, {
          enableArrayArgs: false
        });
      }
      moduleConfig = this.getConfigForAddOperationData(result, moduleConfig, currentOperation);
    } else if (operationType === OperationType.deleteAllOperationData) {
      this.clearAllOperationData();
    }

    return moduleConfig;
  }

  /**
   * Modify and return module config if operation type is addOperationData
   * @param {any} result Data for operation data array
   * @param {any} moduleConfig Module config which contains module name and operations
   * @param {Operation} currentOperation Current operation from current action
   * @returns {any} Modify and return module config depends on operation type addOperationData
   */
  private getConfigForAddOperationData(result: any, moduleConfig: any, currentOperation: Operation): any {
    const operationName = currentOperation.name as string;
    let mappingName = currentOperation.mappingName || operationName;
    if (!_.isNull(this.aggregatedResults) && _.isArray(this.aggregatedResults)) {
      this.aggregatedResults.push(result);
      _.set(this.appOperationData, mappingName, this.aggregatedResults.slice());
    } else {
      _.set(this.appOperationData, mappingName, result);
    }
    if (currentOperation.persistent) {
      _.set(this.appAllPersistentData, mappingName, true);
    }
    return moduleConfig;
  }

  /**
   * Formats operation data to display in debug mode.
   * @param {Operation} currentOperation Current operation from current action.
   * @return {string}
   */
  // <debug>
  private static formatOperationToString(currentOperation: Operation): string {
    const keys = Object.keys(currentOperation);
    const keysLength = keys.length;
    let info = '';

    if (keysLength) {
      info = '/**\n';
      let key;
      for (let i = 0; i < keysLength; i++) {
        key = keys[i];
        if (key === 'data' && _.isObject(currentOperation[key])) {
          try {
            // try/catch is essential because JSON.stringify can't encode large complex objects.
            // This part of code is only executed in DEVELOPMENT mode when we DEBUG dispatcher operations!
            currentOperation[key] = JSON.stringify(currentOperation[key]);
          } catch (e) {
            currentOperation[key] = currentOperation[key].toString();
          }
        }
        info += `* ${key}: "${_.get(currentOperation, key)}"\n`;
      }
      info += '**/\n\n';
    }

    return info;
  }

  // </debug>

  static clearSpacialVariableName = (variableName) => {
    const reg = /^{{[a-zA-Z_]+[\w_]*}}$/;
    if (_.isString(variableName)) {
      if (reg.test(variableName)) {
        variableName = variableName.replace(/[{}]/g, '');
      }
    } else if (_.isArray(variableName)) {
      variableName = variableName.map((argItem) => {
        if (reg.test(argItem)) {
          argItem = argItem.replace(/[{}]/g, '');
        }
        return argItem;
      });
    }
    return variableName;
  }

  /**
   * Returns result of module's operation function.
   * Executes current module's operation function with passed argument.
   * Method will concatenate string with module, operationFn and arg.
   * Concatenated string will be passed to Function constructor as a function body argument with module's scope.
   * Concatenated string example:
   *      'return this.getView().getSelectionModel().isAllSelectedAcrossPages(arg)'
   *
   * Example usage:
   *      var module = this; // module reference where passed function will be executed
   *      var operationFn = 'getView().getSelectionModel().isAllSelectedAcrossPages'; // contains single method or chained methods, last method should be written as "reference" without parentheses
   *      var arg = {folderId: 'TM'}; // values that are passed as arguments of operationFn
   *      var result = this.getResult(module, operationFn, arg);
   *
   * @param {React.Component} module Current module (scope of this).
   * @param {string} operationFn Value of current operation function value stored in appModuleConfig property.
   * @param {any} arg Operation's arguments.
   * @param {any} additionalResultObj
   * @param executeDelay
   * @returns {any}
   */
  private getResult(module: any, operationFn: string, arg: any, additionalResultObj: any, executeDelay: boolean = false, lastExecutedModule: boolean = false): any {
    let result = null;
    let { delay, args, debug, stopExecOperations, synchronous } = additionalResultObj.currentOperation;
    if (!delay) {
      delay = 0;
    }
    if (delay > 0 && executeDelay) {
      this.stopExecOperations = !!(stopExecOperations && synchronous);
      // additionalResultObj.currentOperation.delay = -1;
      _.delay(this.getResult.bind(this, module, operationFn, arg, additionalResultObj, false, lastExecutedModule), delay);
    } else {
      args = DispatcherManager.clearSpacialVariableName(args);
      let argName = _.isArray(arg) && !debug ? args.join(',') : 'arg';
      let fnBody = `return this.${operationFn}(${argName});`;

      if (arg.scope) {
        arg.scope = DispatcherManager.functionExecution('', `return this.${arg.scope}`, 'call', module, undefined);
      }

      let debuggingData = DispatcherManager.debugMode({
        argName,
        fnBody,
        operation: additionalResultObj.currentOperation,
        operationFn,
        module
      });

      argName = DispatcherManager.clearSpacialVariableName(debuggingData.argName);
      fnBody = debuggingData.fnBody;
      module = debuggingData.module;
      if (_.isString(argName) && argName !== 'arg' && _.isArray(arg)) {
        arg = arg[0];
      }

      if (operationFn === DispatcherManager.EXECUTE_NATIVE_METHOD || (Array.isArray(argName) && !argName.length)) {
        argName = 'arg';
      }

      // apply allows us to invoke the function with arguments as an array.
      // call requires the parameters to be listed explicitly
      const invokeFunction = _.isArray(argName) ? 'apply' : 'call';

      try {
        result = DispatcherManager.functionExecution(argName, fnBody, invokeFunction, module, arg);
      } catch (e) {
        result = undefined;
        // <debug>
        if (this.DEVELOPMENT.ENABLE_OPERATION_DEBUG) {
          result = {
            dispatcherEditor: true,
            error: module.moduleName + ' module: ' + e.message.replace('this.', '')
          };
        }
        // </debug>
      }

      if (!executeDelay) {
        // const { lastExecutedModule } = additionalResultObj;
        this.getModuleConfig(result, additionalResultObj.moduleConfig, additionalResultObj.currentOperation);
        if (additionalResultObj.currentOperation.synchronous) {
          this.stopExecOperations = false;
          if (lastExecutedModule) {
            this.backToDispatcher();
          }
        } else if (lastExecutedModule) {
          this.backToDispatcher(additionalResultObj.currentOperation.subName, '');
        }
      }
      // <debug>
      else if (additionalResultObj.currentOperation.subName) {
        error('You can not put subName if delay is 0!');
      }
      // </debug>
    }
    return result;
  }

  /**
   * Returns expression that based on template expression from dispatcher configuration
   * @param {string} expression
   * @param {boolean} executeEval if true it will be executed eval command
   * @param {Operation} operation
   * @returns {string/boolean/any}
   */
  public getTemplateExpression(expression: string, executeEval: boolean, operation?: Operation): string | boolean | any {
    try {
      let templateExpression = expression
        .replace(/{}/g, 'Object.create(null)')
        .replace(/{/g, 'this.appOperationData.')
        .replace(/}/g, '')
        .replace(/this\.appOperationData\.route\(\)/g, 'this.route()')
        .replace(/this\.appOperationData\.topLevelComponentName/g, 'this.topLevelComponentName')
        .replace(/this\.appOperationData\.numberOfActiveDownloads\(\)/g, 'this.numberOfActiveDownloads()')
        .replace(/encodeHTML\(/g, 'this.encodeHTML(');

      // <debug>
      if (expression.includes('route()')) {
        this.appOperationData['route()'] = this.route(); // added for debug
      }
      if (expression.includes('topLevelComponentName')) {
        this.appOperationData['topLevelComponentName'] = this.topLevelComponentName; // added for debug
      }
      if (expression.includes('numberOfActiveDownloads()')) {
        this.appOperationData['numberOfActiveDownloads()'] = this.numberOfActiveDownloads(); // added for debug
      }
      // </debug>

      if (executeEval) {
        const info = _.isUndefined(operation) ? '' : DispatcherManager.debugModeInfo(operation);
        templateExpression = DispatcherManager.functionExecution('', `${info}return ${templateExpression};`, 'call', this, undefined);
      }
      return templateExpression;
    } catch (e) {
      // <debug>
      error(`This ${expression} is wrong!`, 'info');
      console.error('getTemplateExpression Original Error: ', e);
      // </debug>
    }
  }

  /**
   * Handles binding data
   * @param {object} data
   * @param {any} dataItem
   * @param {boolean} isUpdate
   * @return {any}
   */
  private handleBinding(data: object, dataItem: any, isUpdate: boolean): any {
    let bindItem;
    let bindProp;
    const pattern = /{(\w+|.)+}/;

    for (bindProp in dataItem) {
      if (dataItem.hasOwnProperty(bindProp)) {
        if (pattern.test(bindProp)) {
          bindItem = dataItem[bindProp];
          _.unset(dataItem, bindProp);
          bindProp = this.getTemplateExpression(bindProp, true);
          dataItem[bindProp] = bindItem;
        }
        if (_.has(dataItem, bindProp)) {
          bindItem = dataItem[bindProp];
          if (_.isString(dataItem[bindProp])) {
            dataItem[bindProp] = this.getTemplateExpression(bindItem, true);
          } else {
            if (isUpdate) {
              if (_.isObject(dataItem) || _.isArray(dataItem)) {
                _.set(
                  data,
                  bindProp,
                  this.getBindDataObj(
                    {
                      bind: bindItem
                    },
                    isUpdate
                  )
                );
              }
            }
            // <debug>
            else {
              error(`You can not have property as object into template bind object!!! Property name: "${bindProp}"`);
            }
            // </debug>
          }
        }
      }
    }
    _.assign(data, dataItem);
    _.unset(data, 'bind');
    return data;
  }

  /**
   * Returns true if stackItem has not actionName.
   * @param {string[]} actionNames
   * @param {Operation} stackItem
   * @returns {boolean}
   */
  private static hasNoStackItemWithActionName(actionNames: string[], stackItem: Operation): boolean {
    return !contains(actionNames, stackItem.actionName);
  }

  /**
   * Returns true if stackItem has actionName
   * @param {string} actionName
   * @param {Operation} stackItem
   * @returns {boolean}
   */
  private static hasStackItemWithActionName(actionName: string, stackItem: Operation): boolean {
    return stackItem.actionName === actionName;
  }

  /**
   * Check if current module is collapsed.
   * @param {React.Component} module Current module component
   * @returns {boolean} True if module is collapsed, false otherwise.
   */
  private static isModuleCollapsed(module: any): boolean {
    return module.getCollapsed();
  }

  /**
   * Check if current module is visible (is dom rendered)
   * @param {React.Component} module Current module component
   * @returns {boolean} True if module is visible,false otherwise
   */
  private static isModuleVisible(module: any): boolean {
    let isModuleVisible = false;
    if (_.isFunction(module.isVisible)) {
      isModuleVisible = module.isVisible();
    }
    return isModuleVisible;
  }

  /**
   * Check if current module is displayed (is dom displayed [visible])
   * @param {React.Component} module Current module component
   * @returns {boolean} True if module is displayed,false otherwise
   */
  private static isModuleDisplayed(module: any): boolean {
    let isModuleDisplayed = false;
    if (_.isFunction(module.isDisplayed)) {
      isModuleDisplayed = module.isDisplayed();
    }
    return isModuleDisplayed;
  }

  /**
   * Handles adding and removing persistent data from dispatcher
   * @param {string} type Can be `addPersistentData` or `removePersistentData`
   * @param {string[]} persistent
   */
  private manipulateWithPersistentData(type: OperationType, persistent: string[]): void {
    let isPersistentArray = Array.isArray(persistent);
    switch (type) {
      case OperationType.addPersistentData:
        if (isPersistentArray) {
          this.addPersistentData(persistent as string[]);
        }
        break;
      case OperationType.removePersistentData:
        // <debug>
        if (!isPersistentArray) {
          error('All persistent data will be deleted! Is this intentional?', 'info');
        }
        // </debug>
        this.removePersistentData(isPersistentArray ? (persistent as string[]) : ['All']);
        break;
      default:
        break;
    }
  }

  /**
   * Maps add persistent data.
   * @param {string} prop
   */
  private mapPersistentDataAdd(prop: string): void {
    this.appAllPersistentData[prop] = true;
  }

  /**
   * Mask or unmask module component by moduleName.
   * @param {MaskOperation | UnmaskOperation} operation
   */
  private maskOperations(operation: MaskOperation | UnmaskOperation): void {
    if (operation.evaluate) {
      operation = this.evaluateOperationValue(operation) as MaskOperation | UnmaskOperation;
      _.unset(operation, 'evaluate');
    }

    let { moduleName, localeProperty = DispatcherManager.DEFAULT_MASK_LOCALIZATION_MSG } = operation;

    let module;
    let moduleGroup: any[] = this.getModuleGroup(moduleName, operation, true);
    for (let i = 0; i < moduleGroup.length; i++) {
      module = moduleGroup[i];
      let operationFn = '';
      let argName;
      switch (operation.type) {
        case OperationType.maskModule:
          operationFn = 'mask';
          argName = 'message';
          const { className, maskType } = operation;
          if (!operation.debug) {
            module.mask(localeProperty, className, maskType);
          }
          break;
        case OperationType.unmaskModule:
          operationFn = 'unmask';
          argName = '';
          if (!operation.debug) {
            module.unmask();
          }
          break;
        default:
          break;
      }

      // <debug>
      if (operation.debug) {
        let fnBody = `return this.${operationFn}(${argName});`;
        let debuggingData = DispatcherManager.debugMode({
          argName,
          fnBody,
          operation,
          operationFn,
          module
        });
        argName = debuggingData.argName;
        fnBody = debuggingData.fnBody;
        module = debuggingData.module;
        // apply allows us to invoke the function with arguments as an array.
        // call requires the parameters to be listed explicitly
        DispatcherManager.functionExecution(argName, fnBody, _.isArray(argName) ? 'apply' : 'call', module, argName);
      }
      // </debug>
    }

    // <debug>
    this.logExecution(
      Object.assign(operation, {
        name: operation.type === OperationType.maskModule ? 'mask' : 'unmask'
      }),
      true
    );
    // </debug>
    this.backToDispatcher();
  }

  private unmaskAllModuleOperations = (operation: any = {}) => {
    this.setOperationData({
      ...operation,
      data: {
        unmaskAllModule: true
      }
    }, false);
  };

  /**
   * Removes stack actions on base action names
   * @param {string[]} actionNames List of names that should be removed
   */
  private removeActionFromStack(actionNames: string[]): void {
    if (actionNames.length) {
      this.appStack = this.appStack.filter(DispatcherManager.hasNoStackItemWithActionName.bind(this, actionNames));
    }
  }

  /**
   * Removes passed properties from `appAllPersistentData`, if passed array contains `All`, all persistent data will be deleted
   * @param {String[]} propertiesToDelete
   */
  private removePersistentData(propertiesToDelete: string[]): void {
    if (propertiesToDelete.indexOf('All') !== -1) {
      // <debug>
      if (propertiesToDelete.length > 1) {
        const colorBlack = 'color: black;';
        const colorRed = 'color: red;';
        let message: string[] = ['%cAll%c is reserved word. When %cAll%c is passed, all persistent data will be removed!', colorBlack, colorRed, colorBlack, colorRed];
        error(message);
        error('Remove other properties from `persistentData` array for better readability!', 'warn');
      }
      // </debug>
      this.appAllPersistentData = {};
    } else {
      propertiesToDelete.map(this.removePersistentDataMapper.bind(this));
    }
  }

  /**
   * Remove persistent data property from `appAllPersistentData`
   * @param {string} prop
   */
  private removePersistentDataMapper(prop: string) {
    _.unset(this.appAllPersistentData, prop);
  }

  /**
   * Returns internal dispatcher based on type of error
   * @param {Object} showWarning
   * @param {Boolean} skipTranslation
   * @param {String} code
   * @param {String} errorCode
   * @param {String} className
   */
  private showRequestFailDispatcherAction = (showWarning: any, skipTranslation: boolean, code?: string, errorCode?: string, className?: string) => {
    let items: ButtonActionConfig[] = [];
    if (showWarning.hasClose) {
      items.push({
        close: true,
        btnName: skipTranslation ? i18n.t('Dialog.FileList.close') || 'close' : `Error.${code}.close`,
        btnConfig: {
          fillMode: 'flat',
          pos: Position.end
        }
      });
    }
    if (showWarning.hasCancel) {
      items.push({
        close: true,
        btnName: skipTranslation ? i18n.t('Dialog.FileList.cancel') || 'cancel' : `Error.${code}.cancel`,
        btnConfig: {
          fillMode: 'flat',
          pos: Position.end
        }
      });
    }
    if (showWarning.hasLogin) {
      items.push({
        operations: [{
          name: 'redirectToLogin',
          internalOperation: true,
          type: OperationType.action
        }],
        btnName: `Error.${code}.login`,
        btnConfig: {
          themeColor: 'primary',
          pos: Position.end
        }
      });
    }

    const route = this.route();

    if (showWarning.hasTryToLogin && route !== 'login') {
      items.push({
        operations: [{
          name: 'maskTopLevelComponent',
          type: OperationType.action,
          internalOperation: true
        }, {
          name: 'signinSilentRetry',
          internalOperation: true,
          type: OperationType.action
        }, {
          moduleName: 'AuthService',
          name: 'isAuthenticated',
          internalOperation: true,
          type: OperationType.addOperationData
        }, {
          condition: '{isAuthenticated}',
          conditionOperations: [{
            data: {
              path: route,
              forceRefresh: true
            },
            internalOperation: true,
            type: OperationType.redirect
          }],
          elseOperations: [{
            name: 'signedOutWarning',
            internalOperation: true,
            type: OperationType.action
          }],
          internalOperation: true,
          type: OperationType.conditional
        }, {
          internalOperation: true,
          name: 'unmaskTopLevelComponent',
          type: OperationType.action
        }], // TODO add action to retry silent login
        btnName: `Error.${code}.login_retry`,
        btnConfig: {
          themeColor: 'primary',
          pos: Position.end
        }
      });
    }

    if (showWarning.hasTryToLoginOK && route !== 'login') {
      const operations: Action = [{
        name: 'maskTopLevelComponent',
        type: OperationType.action,
        internalOperation: true
      }, {
        name: 'signinSilentRetry',
        internalOperation: true,
        type: OperationType.action
      }, {
        moduleName: 'AuthService',
        name: 'isAuthenticated',
        internalOperation: true,
        type: OperationType.addOperationData
      }, {
        condition: '{isAuthenticated}',
        conditionOperations: [{
          data: {
            path: route,
            forceRefresh: true
          },
          internalOperation: true,
          type: OperationType.redirect
        }],
        elseOperations: [{
          name: 'signedOutWarning',
          internalOperation: true,
          type: OperationType.action
        }],
        internalOperation: true,
        type: OperationType.conditional
      }, {
        internalOperation: true,
        name: 'unmaskTopLevelComponent',
        type: OperationType.action
      }]; // TODO add action to retry silent login
      items.push({
        operations,
        btnName: skipTranslation ? i18n.t('Dialog.FileList.ok') || 'ok' : `Error.${code}.ok`,
        btnConfig: {
          themeColor: 'primary',
          pos: Position.end
        }
      });
      items.push({
        operations,
        btnName: 'ESCAPE'
      });
    }

    if (showWarning.hasOk) {
      items.push({
        operations: [],
        btnName: skipTranslation ? i18n.t('Dialog.FileList.ok') || 'ok' : `Error.${code}.ok`,
        btnConfig: {
          themeColor: 'primary',
          pos: Position.end
        }
      });
    }
    if (showWarning.hasNonExistingUser) {
      items.push({
        operations: [{
          name: 'signOutNonExistingUser',
          type: OperationType.action
        }],
        btnName: skipTranslation ? 'ok' : `Error.${code}.ok`,
        btnConfig: {
          themeColor: 'primary',
          pos: Position.end
        }
      });
    }

    if (showWarning.hasLogoutOk) {
      items.push({
        operations: [{
          name: 'signOutRequestLogOk',
          type: OperationType.action
        }],
        btnName: skipTranslation ? 'ok' : `Error.${code}.ok`,
        btnConfig: {
          themeColor: 'primary',
          pos: Position.end
        }
      });
    }

    if (showWarning.hasEmailLink) {
      if (errorCode) {
        items = [{
          operations: [{
            data: {
              errorCode
            },
            name: 'sendErrorCodeEmail',
            internalOperation: true,
            type: OperationType.action
          }],
          btnName: 'sendEmail',
          btnConfig: {
            themeColor: 'primary',
            pos: Position.end
          }
        }, ...items];
      } else {
        items = [{
          operations: [{
            data: {
              template: {
                subject: '',
                content: '',
                bind: {
                  email: '_.get({initializeUserData}, "administratorEmail") || ""'
                }
              }
            },
            internalOperation: true,
            type: OperationType.data
          }, {
            args: ['email', 'subject', 'content'],
            name: 'sendEmail',
            internalOperation: true,
            moduleName: 'AuthService'
          }],
          btnName: 'sendEmail',
          btnConfig: {
            themeColor: 'primary',
            pos: Position.end
          }
        }, ...items];
      }
    }

    if (items.length === 1) {
      className += ' footer-centered'; // TODO  If there is only 1 button center it,
      // also there is a ticket https://techqa1.translations.com/jira/browse/PDII-25972
      // which will remove this part of code and remove layout configuration from Dispatcher
    }

    return {
      items,
      className
    };
  };

  private showRequestFailMultiErrors(response: any, operation: Operation & RequestOperation, synchronous: boolean = false) {
    const errors = response.errors;
    const error = errors[0] || {};
    const { actionName, mappingName = DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME, handleErrorStatuses } = operation;
    const { showWarning, isRequestError = false, maxUploadSizeExceeded } = error;
    if (isRequestError) {
      const data = _.get(response, 'response.data');
      if (data) {
        this.appOperationData[mappingName] = data;
      }
    }
    let text = '';
    const skipTranslation = true;
    let errorStatusIsHandled;
    if (_.isArray(handleErrorStatuses)) {
      errorStatusIsHandled = handleErrorStatuses.find(errorCode => errorCode === _.get(error, 'response.status'));
    }
    if (showWarning && !errorStatusIsHandled) {
      const { code, placeholders = {}, closable } = showWarning;
      const errorCode = false;
      let className = 'sm file-upload-error footer-centered';
      text = i18n.t(`WarningDialog.Error.${code}.text`, {
        size: _.isUndefined(maxUploadSizeExceeded) ? '' : maxUploadSizeExceeded.toString()
      });
      let title = i18n.t(`WarningDialog.Error.${code}.title`);
      if (!title) {
        title = i18n.t('WarningDialog.Error.DEFAULT_ERROR.title');
      }
      const warningType = WarningType.warning;
      const type = OperationType.dialog;

      const dispatcherActionCfg = this.showRequestFailDispatcherAction(showWarning, skipTranslation, code, '', className);
      className = dispatcherActionCfg.className;
      const items = dispatcherActionCfg.items;
      const moduleContentText = i18n.t('Dialog.FileList.text');

      const gridData = errors.filter((item: any) => !!item).map((item: any) => ({
        name: item.fileName,
        ext: item.fileName.substring(item.fileName.lastIndexOf('.') + 1, item.fileName.length),
        size: getSizeByByte(item.fileSize)
      }));

      let dependenceActions: Operation = {
        data: {
          columns: [{
            title: i18n.t('Dialog.FileList.name'),
            field: 'name',
            width: '300px'
          }, {
            title: i18n.t('Dialog.FileList.ext'),
            field: 'ext'
          }, {
            title: i18n.t('Dialog.FileList.size'),
            field: 'size'
          }],
          data: gridData,
          className: 'tp-uploaded-files-grid tp-no-border',
          rowHeight: 32,
          moduleContentText: moduleContentText
        },
        children: [{
          render: FileToLargeContent,
          propArgs: ['columns', 'data', 'className', 'rowHeight', 'moduleContentText']
        }],
        dependence: operation.subName,
        type,
        actionName,
        className,
        closable,
        items,
        placeholders,
        text,
        title,
        independent: true,
        warningType,
        skipTranslation,
        inserted: true,
        internalOperation: true
      };
      if (synchronous) {
        _.unset(dependenceActions, 'dependence');
        this.appExecutedOperations.push(dependenceActions);
      } else {
        this.appDependenceActions.push(dependenceActions as DialogOperation);
      }
    }
  }

  private showRequestFailWarnings(response: any, operation: Operation) {
    let { showWarning, isRequestError = false } = response;
    const { actionName, mappingName = DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME } = operation;

    if (isRequestError) {
      let data = _.get(response, 'response.data');
      if (data) {
        this.appOperationData[mappingName] = data;
      }
      if (_.get(showWarning, 'badCredentials')) {
        showWarning = null;
        data = _.get(response, 'showWarning');
        if (data) {
          this.appOperationData[mappingName] = data;
        }
      }
    }

    if (showWarning) {
      const { code, placeholders = {}, closable, closeAllPreviousWarning = false } = showWarning;
      let { title, subTitle, text, type, errorCode, className = 'sm' } = showWarning;
      const skipTranslation = !!(title || text);
      text = skipTranslation ? text : `Error.${code}.text`;
      title = skipTranslation ? title : `Error.${code}.title`;

      if (!title) {
        title = i18n.t('WarningDialog.Error.DEFAULT_ERROR.title');
      }

      const warningType = type || WarningType.warning;
      type = OperationType.warning;

      const dispatcherActionCfg = this.showRequestFailDispatcherAction(showWarning, skipTranslation, code, errorCode, className);
      className = dispatcherActionCfg.className;
      const items = dispatcherActionCfg.items;

      if (showWarning.hasLogoutOk) {
        spliceArray(this.appExecutedOperations, [{
          independent: true,
          inserted: true,
          internalOperation: true,
          type: OperationType.unmaskAllModule
        }, {
          independent: true,
          inserted: true,
          data: {
            noReload: true
          },
          type: OperationType.data
        }, {
          independent: true,
          inserted: true,
          internalOperation: true,
          subType: SignType.request,
          type: OperationType.signOut
        }, {
          actionName,
          className,
          closable,
          items,
          placeholders,
          text,
          title,
          subTitle,
          independent: true,
          type,
          warningType,
          inserted: true,
          internalOperation: true,
          skipTranslation
        }], this.appExecutedOperations.length, 0);
      } else {
        spliceArray(this.appExecutedOperations, [{
          independent: true,
          inserted: true,
          internalOperation: true,
          type: OperationType.unmaskAllModule
        }], this.appExecutedOperations.length, 0);
        this.dialogOperation(
          type,
          {
            actionName,
            className,
            closable,
            closeAllPreviousWarning,
            items,
            placeholders,
            text,
            title,
            subTitle,
            independent: true,
            type,
            warningType,
            skipTranslation,
            inserted: true,
            internalOperation: true
          } as DialogOperation | WarningOperation,
          true,
          skipTranslation
        );
      }
    }
  }

  /**
   * Handler of axios onUploadProgress event.
   * @param loaded
   * @param total
   * @param actionName
   * @param fileName
   * @param uploadGroupId
   * @param cancelRequest
   * @param dispatcherId
   */
  private onDispatcherUploadProgress({
    loaded,
    total,
    actionName,
    fileName,
    uploadGroupId
  }: any, cancelRequest: Function, dispatcherId: string) {
    if (fileName && DispatcherManager.UPLOAD[uploadGroupId]) {
      let real = (loaded * 100) / total;
      let rounded: number = Math.round(real);
      if (real > 99.0 && real < 100.0) {
        rounded = 99;
      }

      if (DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] === UPLOAD_REQUEST_CANCEL_SCHEDULED) {
        cancelRequest(DISPATCHER_UPLOAD_CANCELED);
        DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] = UPLOAD_REQUEST_CANCELED;
      } else if (DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] !== UPLOAD_REQUEST_CANCELED) {
        DispatcherManager.UPLOAD[uploadGroupId].DATA[fileName] = rounded;
        DispatcherManager.UPLOAD[uploadGroupId].CANCEL[fileName] = cancelRequest;
      }
      this.updateUploadProgresses(dispatcherId, actionName);
      this.allUploadedInGroup(uploadGroupId);
    }
  }

  private updateUploadProgresses(dispatcherId: string, actionName: string) {
    const uploadData = {
      progresses: _.clone(
        Object.keys(DispatcherManager.UPLOAD).reduce((groupedProgresses: any, uploadGroupId: string) => {
          groupedProgresses[uploadGroupId] = DispatcherManager.UPLOAD[uploadGroupId].DATA;
          return groupedProgresses;
        }, {})
      )
    };

    this.executeAction('DispatcherUploadProgress', {
      dispatcherId,
      persistent: Object.keys(uploadData),
      pos: Position.end
    }, uploadData, {
      // <debug>
      inserted: true,
      actionThatTriggeredInserting: actionName
      // </debug>
    });
  }

  /**
   * Clean up upload group if all files are uploaded or canceled.
   * @param uploadGroupId
   */
  private allUploadedInGroup(uploadGroupId: string) {
    let allUploaded = Object.values(DispatcherManager.UPLOAD[uploadGroupId].DATA).every((percentage: any) => percentage === 100 || percentage === UPLOAD_REQUEST_CANCELED);
    if (allUploaded) {
      delete DispatcherManager.UPLOAD[uploadGroupId];
    }
  }

  createAxios() {
    if (!this.Axios) {
      const {
        DOMAIN,
        NAME,
        PORT,
        PROTOCOL,
        REQUEST_TIMEOUT
      } = Configuration.get().REST_SERVER;
      this.Axios = new Ajax(getApiConfig(PROTOCOL, DOMAIN, PORT, NAME, REQUEST_TIMEOUT));
      this.Axios.onDispatcherUploadProgress = this.onDispatcherUploadProgress.bind(this);
      this.Axios.initializeUploadProgressDataPerGroupUpload = this.initializeUploadProgressDataPerGroupUpload.bind(this);
    }
  }

  /**
   * Creates web socket instance
   */
  createWebSocket() {
    if (!this.webSocket) {
      const {
        WEB_SOCKET
      } = Configuration.get();
      if (!_.isEmpty(WEB_SOCKET)) {
        this.webSocket = new Socket(WEB_SOCKET, this);
      }
    }
  }

  /**
   * Makes ajax request with passed configuration.
   * @param {RequestOperation} operation
   */
  private async request(operation: RequestOperation): Promise<any> {
    this.createAxios();

    let {
      actionName,
      mappingName = DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME,
      data,
      persistent = false,
      synchronous = true,
      handleError = false,
      handleErrorStatuses,
      mergeRequest,
      partOfMergeRequest,
      subName
    } = operation;
    this.stopExecOperations = this.stopExecOperations && synchronous;
    data = this.handleTemplatingAndUpdatingData(_.cloneDeep(data)) as RequestDataBase;
    const isAsyncUpload = !synchronous && data.isUpload;
    data.dispatcherId = isAsyncUpload ? ASYNC_UPLOAD_DISPATCHER_NAME : this.appDispatcherId;
    data.handleError = handleError;
    data.mergeRequest = mergeRequest;
    data.partOfMergeRequest = partOfMergeRequest;
    data.subName = subName;

    let deleteAllOperations = false;
    // <debug>
    this.logExecution(Object.assign(operation, {
      actionName
    }), true);
    // </debug>
    if (synchronous) {
      const response = await this.Axios.request(data, this.appOperationData, mappingName, actionName);
      this.cleanUpRequestData(data.dispatcherId);
      if (persistent) {
        // <debug>
        if (DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME === mappingName) {
          throw new OperationError(`Persisted ajax response data must have "mappingName" different from \`${DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME}\`!`, operation);
        }
        // </debug>
        this.appAllPersistentData[mappingName] = persistent;
      }
      if (response) {
        if (response.showWarning && !operation.handleError) {
          let errorStatusIsHandled;
          if (_.isArray(handleErrorStatuses)) {
            errorStatusIsHandled = handleErrorStatuses.find(errorCode => errorCode === _.get(response, 'response.status'));
          }
          if (!errorStatusIsHandled) {
            // <debug>
            this.appExecutedOperations.splice(1, 0, {
              dispatcherEditor: true,
              dispatcherId: this.appDispatcherId,
              independent: true,
              error: response.message || response.showWarning.text,
              type: 'previousRequestFailedToExecute'
            } as any);
            // </debug>
            this.showRequestFailWarnings(response, operation);
            deleteAllOperations = response.isRequestError;
          }
        } else if (response.isRequestError && response.message === Ajax.MERGE_REQUEST_CANCELED_MESSAGE) {
          // <debug>
          this.appExecutedOperations.splice(1, 0, {
            dispatcherEditor: true,
            dispatcherId: this.appDispatcherId,
            independent: true,
            error: response.message || response.showWarning.text,
            type: 'previousRequestFailedToExecute'
          } as any);
          // </debug>
        } else {
          const hasMultiErrors = !_.isEmpty(response.errors);
          if (hasMultiErrors) {
            this.appOperationData[mappingName] = response.data;
            const multiErrorsMappingName = `${mappingName}_multiErrors`;
            this.appOperationData[multiErrorsMappingName] = response.errors;
          }
          if (hasMultiErrors) {
            this.showRequestFailMultiErrors(response, operation, synchronous);
          }
        }
      }
    } else {
      // <debug>
      if (typeof operation.persistent !== 'boolean') {
        throw new OperationError('Asynchronous requests must have "persistent" property set to `true`!', operation);
      }
      if (typeof mappingName === 'undefined') {
        throw new OperationError('Asynchronous requests must have "mappingName" property!', operation);
      }
      if (DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME === mappingName) {
        throw new OperationError(`Asynchronous requests must have "mappingName" different from \`${DispatcherManager.RESPONSE_OBJECT_DEFAULT_NAME}\`!`, operation);
      }
      // </debug>
      this.appAllPersistentData[mappingName] = true;
      if (operation.mergeRequest) {
        DispatcherManager.asyncRequestInProgress.push(_.cloneDeep(operation));
      }
      this.Axios.request(data, this.appOperationData, mappingName, actionName)
        .then((response: any) => {
          if (response && response.isRequestError && response.message === Ajax.MERGE_REQUEST_CANCELED_MESSAGE) {
            let index: number = _.findIndex(DispatcherManager.asyncRequestInProgress, operation);
            if (index !== -1) {
              spliceArray(DispatcherManager.asyncRequestInProgress, [], index, 1);
            }
            // <debug>
            this.appExecutedOperations.splice(1, 0, {
              dispatcherEditor: true,
              dispatcherId: this.appDispatcherId,
              independent: true,
              error: response.message || response.showWarning.text,
              type: 'previousRequestFailedToExecute'
            } as any);
            // </debug>
            this.backToDispatcher(operation.subName, false, response.isRequestError);
          } else {
            if (response && response.showWarning && !operation.handleError) {
              let errorStatusIsHandled;
              if (_.isArray(handleErrorStatuses)) {
                errorStatusIsHandled = handleErrorStatuses.find(errorCode => errorCode === _.get(response, 'response.status'));
              }
              if (!errorStatusIsHandled) {
                // <debug>
                this.appExecutedOperations.splice(1, 0, {
                  dispatcherEditor: true,
                  dispatcherId: this.appDispatcherId,
                  independent: true,
                  error: response.message || response.showWarning.text,
                  type: 'previousRequestFailedToExecute'
                } as any);
                // </debug>
                this.showRequestFailWarnings(response, operation);
              } else {
                this.appOperationData[mappingName] = response;
                this.backToDispatcher(operation.subName, false, response.isRequestError);
              }
            } else {
              this.appOperationData[mappingName] = response.data;
              const hasMultiErrors = !_.isEmpty(response.errors);
              const multiErrorsMappingName = `${mappingName}_multiErrors`;
              if (hasMultiErrors) {
                this.appOperationData[multiErrorsMappingName] = response.errors;
              }
              if (!operation.mergeRequest) {
                let skip: any = _.find(this.appDependenceActions, {
                  dependence: operation.subName,
                  type: OperationType.unlink
                });

                if (!skip) {
                  this.appDependenceActions.push({
                    actionName: operation.actionName,
                    dependence: operation.subName,
                    items: [mappingName, multiErrorsMappingName],
                    referenceDispatcherId: this.appDispatcherId,
                    internalOperation: true,
                    type: OperationType.syncDispatchersDataItem
                  });
                }
              }
              if (hasMultiErrors) {
                this.showRequestFailMultiErrors(response, operation);
              }
              this.backToDispatcher(operation.subName, false, response.isRequestError);
            }
          }
          this.cleanUpRequestData(this.appDispatcherId);
        });
    }

    this.stopExecOperations = false;
    this.backToDispatcher(false, false, deleteAllOperations);
  }

  /**
   * Sets dispatcher operation data directly without executing real operations.
   * Format data is as same as other action or operation data (template, update, bind...).
   * If you wanted to analyze persistent data from action type you should put actionType argument.
   * If you wanted to convert actionType to object you should set actionTypeToObject to true
   *
   * @param operation
   * @param {boolean} actionTypeToObject
   */
  private setOperationData(operation: Operation, actionTypeToObject: boolean) {
    let { data, specialData, actionType } = operation;
    let resultData = this.handleTemplatingAndUpdatingData(_.cloneDeep(data));
    this.setSpecialOperationData(specialData);
    // <debug>
    this.showInDispatcherEditor(operation, resultData);
    // </debug>
    if (actionType) {
      let actionTypeObj = actionType;
      if (actionTypeToObject) {
        actionTypeObj = DispatcherManager.getActionTypeObj(actionType);
      }
      const { persistent = [] } = actionTypeObj;

      if (Array.isArray(persistent)) {
        persistent.map(this.mapPersistentDataAdd.bind(this));
      }
      // <debug>
      else {
        throw new Error('"persistent" property has to be array of strings when passed as a part of "actionType" property!');
      }
      // </debug>
    }
    this.backToDispatcher();
  }

  /**
   * Sets special operation data like react component
   * @param {any} specialData
   */
  private setSpecialOperationData = (specialData) => {
    if (!_.isEmpty(specialData)) {
      this.appSpecialOperationData = {
        ...this.appSpecialOperationData,
        ...specialData,
        dispatcherId: this.appDispatcherId
      };
    } else {
      this.appSpecialOperationData = {
        ...this.appSpecialOperationData,
        dispatcherId: this.appDispatcherId
      };
    }
  }

  /**
   * Adds new stack action.
   * @param {string} actionName
   * @param {ActionType} actionTypeObj
   * @param {any} data
   * @param {devType} dev
   */
  private setStack(actionName: string, actionTypeObj: ActionType, data: any, dev: devType = {}): void {
    const actionTypeTemplate = _.get(actionTypeObj, 'template');
    if (actionTypeTemplate) {
      actionTypeObj = this.getBindDataObj(actionTypeTemplate);
    }

    const dataTemplate = _.get(data, 'template');
    if (dataTemplate) {
      data = this.getBindDataObj(dataTemplate);
    }

    const dataUpdate = _.get(data, 'update');
    if (dataUpdate) {
      data = this.getBindDataObj(dataUpdate);
    }

    if (actionTypeObj.buffer) {
      actionTypeObj = DispatcherManager.getBufferActionType(actionTypeObj, actionName);
    }
    if (actionTypeObj.canceledStackActions) {
      this.removeActionFromStack(actionTypeObj.canceledStackActions);
    }
    const newStackItems = {
      actionName,
      actionType: actionTypeObj,
      // <debug>
      debug: dev.debug,
      log: dev.log,
      // </debug>
      data
    };

    if (actionTypeObj.pos === Position.start) {
      insert(this.appStack, 0, [newStackItems]);
    } else {
      this.appStack.push(newStackItems as Operation);
    }
  }

  /**
   * Runs stack operation
   * @param {StackOperation} operation
   */
  private stackOperation(operation: StackOperation): void {
    let { name, evaluate = false } = operation;
    // <debug>
    if (!evaluate && name && name.match(/\?/)) {
      throw new OperationError(`Action name "${name}" should be evaluated. Please provide evaluate property set to true.`, operation);
    }
    // </debug>
    if (evaluate) {
      name = this.getTemplateExpression(name, true);
    }
    this.setStack(name, operation.actionType, operation.data, {
      // <debug>
      debug: operation.debug,
      log: operation.log,
      // @ts-ignore
      inserted: true,
      actionThatTriggeredInserting: operation.actionName
      // </debug>
    });
    this.backToDispatcher();
  }

  /**
   * Starts with executing stack action
   * @returns {boolean}
   */
  private startStackAction(): boolean {
    let endOfSession = true;
    if (!_.isEmpty(this.appStack)) {
      endOfSession = false;
      let action = this.appStack[0] as StackOperation;
      let actionName = action.actionName as string;
      let actionType = action.actionType as ActionType;
      let delay = actionType.delay;
      let autoCancelAction = -1;
      if (!delay) {
        delay = 0;
      }
      if (actionType.canceledStackActions) {
        autoCancelAction = actionType.canceledStackActions.indexOf(actionName);
        this.setStack(actionName, actionType, action.data);
      }
      let executed = true;
      let loop = actionType.loop as any; // TODO add type for loop
      if (loop && loop.condition) {
        this.appOperationData.counter = loop.counter;
        executed = this.getTemplateExpression(loop.condition, true);
        // _.unset(this.appOperationData, 'counter');
      } else {
        _.unset(this.appOperationData, 'counter');
      }
      if (executed) {
        if (delay) {
          if (autoCancelAction !== -1) {
            const actionTime = Date.now();
            action.startTime = actionTime;
            this.appDeferredCounter[actionName] = actionTime;
          }
          _.delay(this.executeStackAction.bind(this, action), delay);
        } else {
          this.executeStackAction(action, true);
        }
      } else {
        removeAt(this.appStack, 0);
        if (_.get(loop, 'breakOperations.length')) {
          spliceArray(this.appExecutedOperations, loop.breakOperations, 0, 0);
          this.executeCurrentOperation();
        }
      }
    }
    return endOfSession;
  }

  /**
   *
   * @param partialIds
   * @param dispatcherId
   */
  reduceDispatcherIds = (partialIds: string[], dispatcherId: string) => {
    const allDispatcherIds = Object.keys(this.appDispatchers);
    for (let i = 0; i < allDispatcherIds.length; i++) {
      if (allDispatcherIds[i].split(ASYNC_DOWNLOAD_DISPATCHER_PARTIAL_MATCH_SPLITTER)[0] === dispatcherId) {
        partialIds.push(allDispatcherIds[i]);
      }
    }
    return partialIds;
  };

  /**
   * Gets partial match dispatcher ids.
   * @param dispatcherIds
   */
  getPartialMatchDispatcherIds = (dispatcherIds: string[]): string[] => dispatcherIds.reduce(this.reduceDispatcherIds, []);

  /**
   * Stops all operations, stack and operation data depending on you want to be stopped
   * @param {StopAllOperation} operation
   */
  private stopAll(operation: StopAllOperation): void {
    let defaultItems = [Stop.operations, Stop.stacks];
    let {
      data = false,
      dispatcherIds = [this.appDispatcherId],
      items = defaultItems,
      partialMatch = false
    } = operation;
    const allDispatcherIds = Object.keys(this.appDispatchers);

    if (_.includes(dispatcherIds, 'all')) {
      // <debug>
      if (dispatcherIds.length > 1) {
        throw new OperationError(`When type of action is "${OperationType.stopAll}" and dispatcherIds property contains "all", then all other dispatcherIds are redundant. Please remove them and leave just "all"!`, operation);
      }
      // </debug>
      dispatcherIds = allDispatcherIds;
    }

    if (partialMatch) {
      dispatcherIds = this.getPartialMatchDispatcherIds(dispatcherIds);
    }

    // <debug>
    const difference = _.difference(dispatcherIds, allDispatcherIds);
    if (difference.length && allDispatcherIds.length > 1) {
      throw new OperationError(`Dispatchers with ids of "${difference.join(', ')}" doesn't exist!`, operation);
    }
    // </debug>
    if (_.includes(items, Stop.all)) {
      if (items.length === 1) {
        items = defaultItems;
      }
      // <debug>
      else {
        throw new OperationError(`When type of action is "${OperationType.stopAll}" items property can have values "${Stop.stacks}", "${Stop.operations}" or "${Stop.all}"!`, operation);
      }
      // </debug>
    }

    if (dispatcherIds.includes(this.appDispatcherId)) {
      if (_.includes(items, Stop.operations)) {
        this.appExecutedOperations = [];
      }
      if (_.includes(items, Stop.stacks)) {
        this.appStack = [];
      }
      if (data) {
        this.appOperationData = {};
      }
    }

    if (!operation.isAnotherDispatchers) {
      const operationAnotherDispatchers = {
        ...operation,
        isAnotherDispatchers: true
      };
      const action: Action = [{
        ...operationAnotherDispatchers,
        internalOperation: true,
        // <debug>
        debug: operationAnotherDispatchers.debug,
        log: operationAnotherDispatchers.log
        // </debug>
      }];
      let dispatcher;
      for (let i = 0; i < dispatcherIds.length; i++) {
        dispatcher = this.appDispatchers[dispatcherIds[i]];
        if (dispatcher && dispatcher !== this) {
          if (dispatcher.appCurrentOperation) {
            spliceArray(dispatcher.appExecutedOperations, action, 1, 0);
          } else {
            spliceArray(dispatcher.appExecutedOperations, action, 0, 0);
            dispatcher.executeCurrentOperation();
          }
        }
      }
    }

    this.backToDispatcher();
  }

  /**
   * Switches to group of operations on based condition. Condition type can be switch or conditional.
   * @param {Operation} currentOperation
   * @param {boolean} conditional If true switch is conditional type (if-else)
   */
  private switchOperations(currentOperation: ConditionOperation | SwitchOperation, conditional: boolean = false): void {
    const expression = this.getTemplateExpression(currentOperation.condition, true, currentOperation as Operation);
    let conditionId: string = uuid();
    let operations = [];
    // <debug>
    let moduleName = 'CONDITION';
    // </debug>
    if (conditional) {
      let { conditionOperations, elseOperations } = currentOperation as ConditionOperation;
      operations = expression ? conditionOperations : elseOperations;
    } else {
      // <debug>
      if (_.isBoolean(expression)) {
        throw new OperationError(`Condition ${currentOperation.condition} is evaluating to boolean value ${expression}. Use type ${OperationType.conditional} instead of ${OperationType.switch}!`, currentOperation);
      }
      moduleName = 'SWITCH';
      // </debug>
      let { defaultOperations } = currentOperation as SwitchOperation;
      let value = _.get(currentOperation, expression);
      operations = !value && defaultOperations ? defaultOperations : value;
    }
    if (Array.isArray(operations)) {
      operations = this.createOperationProperties(operations, currentOperation.actionName as string);
      operations = _.cloneDeep(operations);
      // <debug>
      if (!this.conditionOrder[this.appDispatcherId]) {
        this.conditionOrder[this.appDispatcherId] = [];
      }
      this.conditionOrder[this.appDispatcherId].push(conditionId);

      operations = operations.map((o: any) => {
        o.parentCid = conditionId;
        return o;
      });
      operations.push({
        conditionId,
        dispatcherEditor: true,
        dispatcherId: this.appDispatcherId,
        independent: true,
        condition: currentOperation.condition,
        type: 'endOfCondition'
      });
      // </debug>
      spliceArray(this.appExecutedOperations, operations, 1, 0);
    }
    // <debug>
    this.appOperationData[`__${currentOperation.condition}__`] = this.getTemplateExpression(currentOperation.condition, true); // added for debug
    // purposes
    this.logExecution(Object.assign(currentOperation, {
      conditionId
    }), true);
    // </debug>
    this.backToDispatcher();
  }

  /**
   * Synchronizes one or more or all operation data properties between different dispatcher instances.
   * @param {SyncDispatcherItemsOperation} operation
   */
  private syncDispatchersDataItem(operation: SyncDispatcherItemsOperation): void {
    let { items, referenceDispatcherId, dispatcherIds = [], actions = [] } = operation;
    const syncAllData = _.includes(items, 'all');
    // <debug>
    if (syncAllData && items.length > 1) {
      throw new OperationError('If "all" is passed in itemNames all other items are unnecessary! Please provide just "all".', operation);
    }
    // </debug>
    referenceDispatcherId = !referenceDispatcherId || referenceDispatcherId === 'currentDispatcherId' ? this.appDispatcherId : referenceDispatcherId;

    const referenceDispatcher = this.appDispatchers[referenceDispatcherId];
    if (referenceDispatcher) {
      for (const dispatcherId in this.appDispatchers) {
        if (dispatcherIds.length > 0 && !dispatcherIds.includes(dispatcherId)) {
          continue;
        }
        if (dispatcherId !== referenceDispatcherId && this.appDispatchers.hasOwnProperty(dispatcherId)) {
          // <debug>
          let syncKey: string = `${referenceDispatcherId}-${dispatcherId}`;
          if (!DispatcherManager.syncCounter[syncKey]) {
            DispatcherManager.syncCounter[syncKey] = 0;
          }
          DispatcherManager.syncCounter[syncKey]++;

          this.showInDispatcherEditor({
            actionName: operation.actionName,
            referenceDispatcherId,
            dispatcherEditor: true,
            independent: true,
            dispatcherId,
            text: DispatcherManager.syncCounter[syncKey],
            type: 'syncingData'
          } as any, {});
          // </debug>

          let dispatcher = this.appDispatchers[dispatcherId];
          let actionName;
          for (let i = 0; i < actions.length; i++) {
            actionName = actions[i];
            if (actionName === 'all') {
              _.assign(dispatcher.appActions, referenceDispatcher.appActions);
              break;
            } else {
              _.set(dispatcher.appActions, actionName, referenceDispatcher.appActions[actionName]);
            }
          }
          if (syncAllData) {
            _.assign(dispatcher.appOperationData, referenceDispatcher.appOperationData);
            continue;
          }
          for (let index = 0; index < items.length; index++) {
            const itemName = items[index];
            if (itemName) {
              dispatcher.appOperationData[itemName] = referenceDispatcher.appOperationData[itemName];
            }
          }
        }
      }
    }
    // <debug>
    else {
      throw new OperationError(`Dispatcher with id "${referenceDispatcherId}" doesn't exist! Provide existing dispatcherId for referenceDispatcherId property.`, operation);
    }
    // </debug>
    this.backToDispatcher();
  }

  // <debug>
  /**
   * Displays operation that is running and running time as console log
   * @param {string} operationActionName
   * @param {string} moduleName
   * @param {string} operationName
   * @param {boolean} operationEnd If true operation is finished
   * @param {boolean/string} log
   */
  private timeLog(operationActionName: string, moduleName: string, operationName: string, operationEnd: boolean, log: boolean | string = false): void {
    if (this.appCurrentOperation) {
      this.showInDispatcherEditor(this.appCurrentOperation);
    } else {
      this.showInDispatcherEditor({
        dispatcherEditor: true,
        independent: true,
        inserted: true,
        dispatcherId: this.appDispatcherId,
        type: 'readyForNextAction'
      });
    }
    if (this.DEVELOPMENT.ENABLE_OPERATION_TIME_LOGGING) {
      if (this.appTimeLogParams !== null) {
        const { operationActionName, moduleName, operationName, startTime } = this.appTimeLogParams;
        const time: number = Date.now() - startTime;
        const operationDisplay: string = `Action: ${operationActionName};\nModule: ${moduleName};\nOperation: ${operationName}\ntime: ${time};\nDispatcherID: ${this.appDispatcherId}\n`;
        if (this.appTimeLogParams.log) {
          let color = this.DEVELOPMENT.LOG_ACTION_NAMES[operationActionName] || this.DEVELOPMENT.OPERATION_LOG_COLOR;
          messageWithColors(`%c${operationDisplay}%c`, color, 'log');
        } else {
          console.log(operationDisplay);
        }
      }
      this.appTimeLogParams = null;
      if (operationEnd) {
        this.appTimeLogParams = {
          log,
          moduleName,
          operationActionName,
          operationName,
          startTime: Date.now()
        };
      }
    } else if (log) {
      const time: number = Date.now();
      const operationDisplay: string = `Action: ${operationActionName};\nModule: ${moduleName};\nOperation: ${operationName} -->> ${time};\nDispatcherID: ${this.appDispatcherId}\n`;
      let color = this.DEVELOPMENT.LOG_ACTION_NAMES[operationActionName] || this.DEVELOPMENT.OPERATION_LOG_COLOR;
      messageWithColors(`%c${operationDisplay}%c`, color, 'log');
    }
  }

  // </debug>
  // <debug>
  /**
   * Displays generated operation that is running.
   * @param {Operation} operation
   * @param {boolean/string} log
   */
  private timeLogDynamic(operation: Operation, log: boolean | string = false): void {
    if (this.DEVELOPMENT.ENABLE_OPERATION_TIME_LOGGING) {
      let { moduleName, actionName, data } = operation;
      let fn = this.handleTemplatingAndUpdatingData(_.cloneDeep(data)).fn;
      if (!_.isString(moduleName)) {
        moduleName = `DYNAMIC (${moduleName})`;
        fn = `FUNCTION (${fn})`;
      }
      const message = [`%cAction:\t${actionName}\nModule:\t${moduleName}\nOperation:\t${fn}\nDispatcherID:\t${this.appDispatcherId}`, `background: ${log ? this.DEVELOPMENT.OPERATION_LOG_COLOR : 'white'}; color: black`];
      error(message, 'info');
    }
  }

  // </debug>

  private getDialogChildren(child: ChildrenType): ChildrenType {
    let props = (child.propArgs || []).reduce((acc: { [key: string]: any }, prop: string) => _.set(acc, prop, _.get(this.appOperationData, prop)), {});
    _.set(props, 'zIndexCls', `${DispatcherWindowManager.zIndexClsPrefix}${_.get(this, 'appCurrentOperation.type')}`);
    return {
      props,
      render: child.render
    } as ChildrenType;
  }

  private getChildrenPropsData = (children: ChildrenType[]) => {
    let data = {};
    let props;
    if (children && Array.isArray(children)) {
      children.forEach((child: ChildrenType) => {
        props = (child.propArgs || []).reduce((acc: { [key: string]: any }, prop: string) => _.set(acc, prop, _.get(this.appOperationData, prop)), {});
        _.assign(data, props);
      });
    }
    return data;
  };

  /**
   * Create dialog or warning operation to invoke creating dialog or warning module trough dispatcher.
   * @param type
   * @param operation
   * @param deleteActionGroup
   * @param skipTranslation Used to indicate that text and title are already translated on backend side so there is
   * no reason for frontend side to localise it.
   */
  private dialogOperation(type: OperationType, operation: DialogOperation | WarningOperation, deleteActionGroup: boolean | string = false, skipTranslation: boolean = false) {
    // <debug>
    let copiedOperation = _.cloneDeep(operation);
    // </debug>
    const items: ButtonActionConfig[] = operation.items;
    let moduleName = 'Dialog';
    let { placeholders, className, closable = true, previousFocus = true, accessibility, closeAllPreviousWarning } = operation;
    let children: any = null;
    let icon: string | null = null;
    switch (type) {
      case OperationType.dialog:
        operation = operation as DialogOperation;
        children = operation.children;
        break;
      case OperationType.warning:
        operation = operation as WarningOperation;
        icon = `tp-${operation.warningType}-icon`;
        moduleName = `Warning${moduleName}`;
        break;
    }

    const hideOperation: Action = [{
      args: ['hideOperationCfgData'],
      data: {
        hideOperationCfgData: {
          dispatcherId: this.appDispatcherId
        }
      },
      moduleName,
      name: 'hide',
      internalOperation: true,
      synchronous: true
    }, {
      name: 'setFocusToActiveDialog',
      internalOperation: true,
      type: OperationType.action
    }];

    if (operation.data) {
      operation.data = this.handleTemplatingAndUpdatingData(operation.data);
    }

    // <debug>
    let childrenCopy = [];
    if (Array.isArray(children)) {
      childrenCopy = _.cloneDeep(children).map(this.mapChild);
    }
    this.showInDispatcherEditor({
      ...copiedOperation,
      children: childrenCopy
    });
    // </debug>

    let buttons: DialogButton[] = this.convertItemsConfigIntoActions(moduleName, items, hideOperation);
    placeholders = this.handleTemplatingAndUpdatingData(placeholders);

    if (children && Array.isArray(children)) {
      this.dialogChildren = children.map((child: ChildrenType) => this.getDialogChildren(child)) as ChildrenType[];
    }
    if (_.isString(className) && className.search(/{[^{}]*}/) !== -1) {
      className = this.getTemplateExpression(className, true);
    }
    const configWarningDialog: DialogConfig = {
      accessibility,
      buttons,
      className,
      closable,
      dispatcherId: this.appDispatcherId,
      icon,
      placeholders,
      previousFocus,
      text: operation.text,
      title: operation.title,
      subTitle: operation.subTitle,
      skipTranslation: skipTranslation || operation.skipTranslation,
      warningType: operation.warningType,
      resizable: operation.resizable
    };

    let dispatcherEditorData: any = {
      // <debug>
      // used in combination with dispatcher editor
      // operation will have inserted and internalOperation properties only when warning is created via
      // showRequestFailWarnings when request operation fails to execute
      inserted: !!(operation as any).inserted,
      internalOperation: !!(operation as any).internalOperation
      // </debug>
    };

    spliceArray(this.appExecutedOperations, [{
      actionName: operation.actionName,
      args: ['configWarningDialog'],
      data: {
        configWarningDialog
      },
      // <debug>
      debug: operation.debug,
      log: operation.log,
      // </debug>
      independent: operation.independent,
      moduleName,
      name: 'show',
      ...dispatcherEditorData,
      internalOperation: true,
      synchronous: true
    }], 1, 0);

    if (closeAllPreviousWarning) {
      spliceArray(this.appExecutedOperations, [{
        index: DialogPosition.allPrevious,
        windowType: WindowType.warning,
        type: OperationType.closeWindow
      }], 2, 0);
    }

    if (deleteActionGroup) {
      deleteActionGroup = operation.actionName as string;
    }
    this.backToDispatcher(false, deleteActionGroup);
  }

  /**
   * Maps children
   * @param {ChildrenType} child
   */
  private mapChild = (child: ChildrenType) => {
    let moduleName = /function (\w+)\(\) {/.exec(child.render);
    child.render = moduleName ? moduleName[1] : child.render;
    return child;
  };

  /**
   * Creates popup window and sets its config
   * @param {PopupWindowOperation} operation
   */
  private popupWindowOperation = (operation: PopupWindowOperation) => {
    const renderTo = operation.moduleName;
    let { title, onCloseOperations, actionName, height, width, placeholders = {}, pos, windowId } = operation;
    let data = this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    let popupWindowId = this.getTemplateExpression(windowId, true);
    if (popupWindowId) {
      windowId = popupWindowId;
    }
    let children = operation.children;
    if (this.appOperationData.redirectPopupWindow) {
      if (children && Array.isArray(children)) {
        children = children.map((child: ChildrenType) => this.getDialogChildren(child)) as ChildrenType[];
      }
      const renderToModule = this.getModuleGroup(renderTo, operation)[0];
      if (renderToModule) {
        renderToModule.setWindowPopupChildren(children);
      }
    } else {
      const data = this.getChildrenPropsData(children);
      const popupWindowServiceData = {
        pos,
        renderTo,
        title,
        onCloseOperations,
        actionName,
        data,
        height,
        width,
        placeholders: this.handleTemplatingAndUpdatingData(_.cloneDeep(placeholders)),
        appOperationData: _.cloneDeep(this.appOperationData),
        appAllPersistentData: _.clone(this.appAllPersistentData),
        dispatcherId: this.appDispatcherId,
        windowId
      };

      // <debug>
      let childrenCopy = [];
      if (Array.isArray(children)) {
        childrenCopy = _.cloneDeep(children).map(this.mapChild);
      }
      // </debug>

      // eslint-disable-next-line array-bracket-newline
      spliceArray(this.appExecutedOperations, [
        // <debug>
        {
          ...operation,
          children: childrenCopy,
          actionName,
          dispatcherEditor: true,
          dispatcherId: this.appDispatcherId,
          editorData: data
        },
        // eslint-disable-next-line array-element-newline
        // </debug>
        {
          actionName,
          args: ['popupWindowServiceData'],
          data: {
            popupWindowServiceData
          },
          // <debug>
          debug: operation.debug,
          log: operation.log,
          // </debug>
          moduleName: 'PopupWindow',
          internalOperation: true,
          name: 'show'
        }], 1, 0);
    }
    this.backToDispatcher();
  };

  /**
   * Sets state object that will be saved throw login process
   * @param {any} state
   */
  public preserveState = (state: any) => {
    this.appAllPersistentData['state'] = true;
    this.appOperationData['state'] = state;
  };

  // <debug>
  /**
   * Send all batched messages from all Dispatcher Managers to Dispatcher Editor if page was reloaded and recording was not stopped manually.
   */
  private sendBatchedMessage = () => {
    let dispatcher: DispatcherManager;

    for (let dispatcherId in this.appDispatchers) {
      if (this.appDispatchers.hasOwnProperty(dispatcherId)) {
        dispatcher = this.appDispatchers[dispatcherId];
        if (dispatcher) {
          dispatcher.DEVELOPMENT.MESSAGES.forEach((message) => {
            window.postMessage(message, '*');
          });
          dispatcher.DEVELOPMENT.MESSAGES = [];
        }
      }
    }
  };

  private static sendGeneratedActionsToDispatcherEditor = (actions: { [key: string]: Action }, ENABLE_OPERATION_DEBUG: boolean = false) => {
    const LOCAL_ENABLE_OPERATION_DEBUG = localStorage.getItem('ENABLE_OPERATION_DEBUG') === 'true';

    if (LOCAL_ENABLE_OPERATION_DEBUG || ENABLE_OPERATION_DEBUG) {
      const msg = {
        actions: JSON.stringify(actions),
        type: 'FROM_DISPATCHER_MANAGER_GENERATED'
      };
      window.postMessage(msg, '*');
    }
  };

  /**
   * Enables operation logging.
   *
   * @param {Boolean} enable
   */
  private setEnableOperationDebug = (enable: boolean) => {
    let dispatcher: DispatcherManager;
    let clearMessages = false;

    if (enable) {
      this.appActions = new Proxy(this.appActions, this.addActionInterceptor());
      this.sendBatchedMessage();
    } else {
      clearMessages = true;
      DispatcherManager.COUNTER = 0;
      localStorage.setItem('DEBUG_COUNTER', '0');
    }

    localStorage.setItem('ENABLE_OPERATION_DEBUG', String(enable));

    for (let dispatcherId in this.appDispatchers) {
      if (this.appDispatchers.hasOwnProperty(dispatcherId)) {
        dispatcher = this.appDispatchers[dispatcherId];
        if (dispatcher) {
          if (clearMessages) {
            dispatcher.DEVELOPMENT.MESSAGES = [];
          }
          dispatcher.DEVELOPMENT.ENABLE_OPERATION_DEBUG = enable;
        }
      }
    }
  };

  /**
   * Sends operation as postMessage to Dispatcher Editor if debugging is enabled or stores operation as message
   * in `DEVELOPMENT.MESSAGES` array if user reloads page without previously stopping debug.
   *
   * @param {Object} operation
   * @param {Object} appOperationData
   */
  private showInDispatcherEditor(operation: any, appOperationData: any = _.cloneDeep(this.appOperationData)) {
    const { ENABLE_OPERATION_DEBUG } = this.DEVELOPMENT;
    const LOCAL_ENABLE_OPERATION_DEBUG = localStorage.getItem('ENABLE_OPERATION_DEBUG') === 'true';

    if (LOCAL_ENABLE_OPERATION_DEBUG || ENABLE_OPERATION_DEBUG) {
      DispatcherManager.COUNTER++;
      localStorage.setItem('DEBUG_COUNTER', String(DispatcherManager.COUNTER));
      const msg = {
        index: DispatcherManager.COUNTER,
        data: JSON.stringify(appOperationData),
        operation: JSON.stringify(operation),
        action: JSON.stringify({
          name: operation.actionName || '',
          dispatcherId: this.appDispatcherId
        }),
        type: 'FROM_DISPATCHER_MANAGER'
      };
      if (this.DEVELOPMENT.ENABLE_OPERATION_DEBUG) {
        this.sendBatchedMessage();
        window.postMessage(msg, '*');
      } else if (LOCAL_ENABLE_OPERATION_DEBUG) {
        this.DEVELOPMENT.MESSAGES.push(msg);
      }
    }
  }

  // </debug>
  /**
   * Updates dialog footer custom tags content (for now)
   * @param {UpdateDialog} operation
   */
  private updateDialogOperation = (operation: UpdateDialog) => {
    // <debug>
    let copiedOperation = _.cloneDeep(operation);
    // </debug>
    this.handleTemplatingAndUpdatingData(operation.data);
    // <debug>
    this.showInDispatcherEditor(copiedOperation, operation.data);
    // </debug>
    const { children = null, customTags = [], buttons = [], scope = UpdateDialogScope.active } = operation;
    let isDisabled;
    let isHidden;
    let buttonStateObj = {};

    if (customTags.length) {
      customTags.forEach((item) => {
        const { args = [], hidden = false, name = '', disabled } = item;
        _.set(buttonStateObj, `customTagArgs_${name.replace(/\./g, '_')}`, this.getOperationArg(args, null, true));
        isHidden = _.isBoolean(hidden) ? hidden : this.getTemplateExpression(hidden, true);
        _.set(buttonStateObj, `customTagHidden_${name.replace(/\./g, '_')}`, isHidden);
        if (!_.isUndefined(disabled)) {
          isDisabled = _.isBoolean(disabled) ? disabled : this.getTemplateExpression(disabled, true);
          _.set(buttonStateObj, `customTagDisabled_${name.replace(/\./g, '_')}`, isDisabled);
        }
      });
    }
    let dialogModules: any[] = this.getModuleGroup('Dialog', operation).concat(this.getModuleGroup('WarningDialog', operation));
    const dialogModule = dialogModules.find(module => DispatcherManager.isModuleVisible(module));
    if (buttons.length) {
      buttons.forEach((item) => {
        const { disabled = false, hidden = false, name = '' } = item;
        // <debug>
        if (!name.length) {
          let msg = 'Button requires unique name to provide enable/disable or show/hide button functionality.';
          throw new OperationError(msg, operation);
        }
        // </debug>
        // if (disabled) {
        isDisabled = _.isBoolean(disabled) ? disabled : this.getTemplateExpression(disabled, true);
        _.set(buttonStateObj, `disabled_${name.replace(/\./g, '_')}`, isDisabled);
        // }
        isHidden = _.isBoolean(hidden) ? hidden : this.getTemplateExpression(hidden, true);
        _.set(buttonStateObj, `hidden_${name.replace(/\./g, '_')}`, isHidden);
      });
    }
    if (dialogModule) {
      if (children && Array.isArray(children)) {
        const renderChildren = children.map((child: ChildrenType) => this.getDialogChildren(child)) as ChildrenType[];
        _.set(buttonStateObj, 'children', renderChildren);
      }
      dialogModule.updateFooterItems(buttonStateObj, scope);
    } else {
      this.backToDispatcher();
    }
  };

  public toDispatcher(forceExecute: boolean = false, dispatcherId: string = this.appDispatcherId): void {
    const dispatcher = this.appDispatchers[dispatcherId] || this;

    if (forceExecute && !dispatcher.appCurrentOperation) {
      spliceArray(dispatcher.appExecutedOperations, [{}], 0, 0);
    }
    dispatcher.backToDispatcher();
  }

  widgetOperation(operation: WidgetOperation): void {
    let {
      title,
      subTitle,
      warningType,
      name = Date.now().toString(),
      items = [],
      placeholders = {},
      children = null,
      className,
      resizable = true,
      draggable = true,
      width = 400,
      height = 400,
      minWidth = 400,
      minHeight = 400,
      previousFocus = true,
      accessibility
    } = operation;
    this.handleTemplatingAndUpdatingData(_.cloneDeep(operation.data));
    // <debug>
    if (!name) {
      throw new OperationError('Name must be provided and it must be unique within widgets!', operation);
    }
    if (!title) {
      throw new OperationError('Missing widget title property!', operation);
    }
    let childrenCopy = _.cloneDeep(children);
    // </debug>

    if (Array.isArray(children)) {
      children = children.map((child: ChildrenType) => this.getDialogChildren(child));
      // <debug>
      childrenCopy = childrenCopy.map((child: ChildrenType) => {
        child.render = child.render.constructor.name;
        return child;
      });
      // </debug>
    }

    let props = {
      accessibility,
      className,
      id: name,
      title,
      subTitle,
      items,
      warningType,
      buttons: this.convertItemsConfigIntoActions(`_${name}`, items, []),
      previousFocus,
      placeholders: this.handleTemplatingAndUpdatingData(placeholders),
      dispatcherId: this.appDispatcherId,
      // widgetConfig
      resizable,
      draggable,
      width,
      height,
      minWidth,
      minHeight
      // end of widgetConfig
    };

    const moduleToRender = DOWNLOAD_INDICATOR_WIDGET === name ? 'DownloadWidget' : 'Widget';
    Connect.show({ render: moduleToRender, props, children });

    // <debug>
    this.logExecution({
      ...operation,
      children: childrenCopy,
      internalOperation: true,
      name: 'show'
    }, true);
    // </debug>
    this.backToDispatcher();
  }

  /**
   * Register services and their actions to dispatcher.
   * @param services
   * @param serviceActions actions that are needed for services
   */
  public useServices(services: { [key: string]: any[] }, serviceActions: { [key: string]: Action }) {
    // <debug>
    let duplicatedModules = _.intersection(Object.keys(this.appModules), Object.keys(services));
    Object.keys(services).reduce((filteredServices: { [key: string]: any[] }, moduleName: string) => {
      // Every module that is instance of Service will be treated as singleton
      // There will be only one instance registered at appModules.
      if (filteredServices[moduleName][0] instanceof BaseService && this.appModules[moduleName]) {
        duplicatedModules.splice(duplicatedModules.indexOf(moduleName), 1);
        _.unset(filteredServices, moduleName);
      }
      return filteredServices;
    }, services);
    if (duplicatedModules.length) {
      throw new Error(`Module can not be overridden "${duplicatedModules}"! Please choose different name for module.`);
    }
    // </debug>

    _.merge(this.appModules, services);

    // <debug>
    let duplicatedActions = _.intersection(Object.keys(this.appActions), Object.keys(serviceActions));
    if (duplicatedActions.length) {
      throw new Error(`Actions can not be overridden! Please choose different name for following actions.\n${duplicatedActions.join('\n')}`);
    }
    if (_.get(this.appActions, DispatcherManager.GENERIC_ACTION_NAME)) {
      throw new Error(`Action name can't be "${DispatcherManager.GENERIC_ACTION_NAME}"! Please choose different name for action.`);
    }
    // </debug>

    _.merge(this.appActions, serviceActions);
  }

  /**
   * Unlink dispatcher or dispatchers. Can't unlink `MAIN_DISPATCHER_ID`.
   * @param operation
   */
  private unlinkDispatcher(operation: UnlinkOperation): void {
    let { dispatcherIds = [this.appDispatcherId], partialMatch = false } = operation;

    if (partialMatch) {
      dispatcherIds = this.getPartialMatchDispatcherIds(dispatcherIds);
    }

    let dispatcherId = null;
    for (let i = 0; i < dispatcherIds.length; i++) {
      dispatcherId = dispatcherIds[i];
      if (dispatcherId !== MAIN_DISPATCHER_ID) {
        this.appDispatchers[dispatcherId] = null;
        delete this.appDispatchers[dispatcherId];
      }
    }

    this.backToDispatcher();
  }

  /**
   * Creates operations and executes to set module state properties that named same as dispatcher variables
   * @param {String} updateStateName Name of variable
   * @param {Object} updateStateValue Value of variable
   */
  public updateStateVariables = (updateStateName: string, updateStateValue: any) => {
    if (this.updateStateModules[updateStateName]) {
      const operations = [];
      this.updateStateModules[updateStateName].forEach((item) => {
        operations.push({
          actionName: this.appCurrentOperation ? this.appCurrentOperation.actionName : '',
          ...item,
          data: {
            updateStateName,
            updateStateValue
          },
          name: 'setDispatcherUpdateState',
          args: ['updateStateName', 'updateStateValue'],
          inserted: true,
          internalOperation: true
        });
      });
      const index = this.appExecutedOperations.length ? 1 : 0;
      spliceArray(this.appExecutedOperations, operations, index, 0);
      if (!this.appCurrentOperation) {
        this.executeCurrentOperation();
      }
    }
  };

  /**
   * Groups uploaded file by its status. Uploaded or rejected.
   * @param mappingName
   */
  private groupFiles = (mappingName: string): {
    uploaded: any[],
    rejected: any[]
  } => {
    const responseFiles: {
      rejected: string,
      uploaded: string
    } = this.appOperationData.responseFiles;
    return this.appOperationData[mappingName]?.data.reduce(function (group, file) {
      if (file[responseFiles.rejected]) {
        group.rejected.push(...file[responseFiles.rejected]);
      } else if (file[responseFiles.uploaded]) {
        group.uploaded.push(...file[responseFiles.uploaded]);
      }
      return group;
    }, {
      uploaded: [],
      rejected: []
    });
  };

  /**
   * Handles uploading operation. Passed operation configuration converts to action that will upload file.
   * @param operation
   */
  private uploadOperation(operation: UploadOperation): void {
    let {
      initialData = {},
      data
    } = operation;
    if (operation.evaluate) {
      operation = this.evaluateOperationValue(operation, ['mappingName', 'uniqueParameter', 'uploadCondition', 'handleErrorStatuses']) as UploadOperation;
      delete operation.evaluate;
    }
    let upload: Upload = new Upload(operation, this.appOperationData, this.appDispatcherId, {
      initialData: this.handleTemplatingAndUpdatingData(initialData),
      data: this.handleTemplatingAndUpdatingData(data)
    }, operation.actionName === 'dropUploadFilesWithoutPrepare');

    let uploadOperations: Action = upload.getOperations();
    // eslint-disable-next-line array-bracket-newline
    spliceArray(this.appExecutedOperations, [
      // <debug>
      ...upload.getComplexOperationStart(operation),
      // eslint-disable-next-line array-element-newline
      // </debug>
      ...uploadOperations], 1, 0);
    upload = null;
    this.backToDispatcher();
  }

  // <debug>
  public testHelper_clearAllOperationData() {
    this.clearAllOperationData();
    this.appCurrentOperation = null;
  }

  /**
   * Set custom Configuration for testing purposes
   * @param {string} apiUrl
   */
  public testHelper_setConfig = (apiUrl: any) => {
    let [full_url, raw_protocol, protocol, domain, port, path] = apiUrl.match(Configuration.apiMatch) || [];
    let [full_path, name, apiPrefix, restOfPath] = path.match(Configuration.pathMatch) || [];
    Configuration.testHelper_set({
      REST_SERVER: {
        PROTOCOL: protocol,
        DOMAIN: domain,
        PORT: port,
        NAME: name,
        REST_API_PREFIX: apiPrefix
      }
    });
  };
  public testHelper_getConfig = () => Configuration.get()
  // </debug>
}

const Dispatcher = DispatcherManager.Instance;

if (!_.get(window, 'Dispatcher')) {
  // @ts-ignore
  Dispatcher.enableLogging = () => {
    _.assign(Dispatcher.DEVELOPMENT, {
      ENABLE_OPERATION_TIME_LOGGING: true
    });
  };
  // @ts-ignore
  Dispatcher.disableLogging = () => {
    _.assign(Dispatcher.DEVELOPMENT, {
      ENABLE_OPERATION_TIME_LOGGING: false
    });
  };
  // @ts-ignore
  Dispatcher.enableDataLogging = () => {
    _.assign(Dispatcher.DEVELOPMENT, {
      OPERATION_LOG_DATA: true
    });
  };
  // @ts-ignore
  Dispatcher.disableDataLogging = () => {
    _.assign(Dispatcher.DEVELOPMENT, {
      OPERATION_LOG_DATA: false
    });
  };
  // @ts-ignore
  Dispatcher.enableProxy = () => {
    DataProxy.ENABLE_DATA_PROXY = true;
    Dispatcher.proxyAppOperationData();
  };
  _.set(window, 'Dispatcher', Dispatcher);

  // @ts-ignore
  window.Dispatcher.disableLogging();}

export default Dispatcher;
